import { useTranslation } from 'react-i18next'
import { localizedName, localizedNameWithEn } from '../utils/localizedName'
import { useEffect, useState, useCallback } from 'react'
import * as XLSX from 'xlsx'
import RepairJobModal from '../components/repair/RepairJobModal'
import AssetQuickView from '../components/assets/AssetQuickView'
import SwapAssetModal from '../components/assets/SwapAssetModal'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box, Text, Button, Table, Badge, Group, TextInput, Select,
  Modal, Stack, Grid, ActionIcon, Tooltip, Loader,
  Center, Image, FileButton, Divider, Alert, ScrollArea, List,
  Timeline, Textarea, NumberInput, Card, SimpleGrid, Checkbox,
  Pagination, Drawer, Indicator, Skeleton,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import SmartDateInput from '../components/SmartDateInput'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconEdit, IconTrash, IconSearch,
  IconAlertTriangle, IconBan, IconCircleCheck, IconFile, IconExternalLink, IconX, IconDownload,
  IconUpload, IconPhoto, IconFileSpreadsheet, IconCamera,
  IconAlertCircle, IconCheck, IconBuilding,
  IconArrowsTransferUp, IconMapPin, IconHistory, IconCopy, IconScan, IconQrcode, IconRefresh, IconTool,
  IconPlugConnected, IconArrowsSort, IconChevronUp, IconChevronDown,
  IconArrowsLeftRight, IconFilter,
} from '@tabler/icons-react'
import {
  getAsset, getAssets, createAsset, updateAsset, deleteAsset, hardDeleteAsset, uploadAssetImage,
  reportAssetLost, markAssetFound, reportAssetDamaged, markAssetInstalled, uninstallAsset,
  uploadLostIncident, uploadDamagedIncident,
  getDepartments, getCategories, getSubCategories, getNextAssetCode,
  transferDepartment, moveLocation, swapLocation, getAssetHistory, getLocations,
  getBrands, getModels, getAssetsPaginated, getVendors, checkAssetDuplicate,
  createBrand, createCategory, createSubCategory, createVendor, createLocation, createModel,
  createMaintenance, getAllAssetLicenseMap, getAllAssetsAdmin, getImportReference, createRepairJob, getInventoryItems,
  scanLookupAsset,
} from '../api/client'
import { modals } from '@mantine/modals'
import api from '../api/client'
import { useAuth } from '../hooks/useAuth'
import { BarcodeScanner } from '../components/BarcodeScanner'
import AssetQRLabel from '../components/AssetQRLabel'

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  AVAILABLE: 'lime', ASSIGNED: 'teal', BORROWED: 'blue', INSTALLED: 'cyan', REPAIR: 'orange',
  DAMAGED: 'red', LOST: 'grape', WRITE_OFF: 'gray',
}

// ── Skeleton ตอนโหลด — แทนวงกลมหมุนๆ เดิมด้วยกรอบเทาๆ ที่มีรูปร่างคล้ายการ์ด/แถวจริง ไม่ได้ทำให้
// โหลดช้าลงเลย (เวลาดึงข้อมูลจริงเท่าเดิมทุกประการ) แค่เปลี่ยนสิ่งที่แสดงระหว่างรอ — ให้ความรู้สึก
// เร็วกว่าเพราะเห็นโครงสร้างล่วงหน้า ไม่ใช่จอว่างๆ มีแค่ spinner กลางจอ ──
function AssetCardSkeleton() {
  return (
    <Box p="md" style={{ borderBottom: '1px solid #f0f0f0' }}>
      <Group wrap="nowrap" gap="sm" align="flex-start">
        <Skeleton height={40} width={40} radius="sm" />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Skeleton height={13} width="55%" radius="sm" mb={6} />
          <Skeleton height={10} width="35%" radius="sm" mb={10} />
          <Group gap={6}>
            <Skeleton height={18} width={64} radius="xl" />
            <Skeleton height={18} width={88} radius="xl" />
          </Group>
        </Box>
      </Group>
    </Box>
  )
}
function AssetRowSkeleton() {
  return (
    <Table.Tr>
      <Table.Td><Skeleton height={14} width={14} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={32} width={32} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={70} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={110} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={60} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={80} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={60} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={70} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={90} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={80} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={18} width={70} radius="xl" /></Table.Td>
      <Table.Td><Skeleton height={12} width={50} radius="sm" /></Table.Td>
      <Table.Td><Skeleton height={12} width={70} radius="sm" /></Table.Td>
    </Table.Tr>
  )
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  assetCode: '', name: '', serialNumber: '',
  departmentCode: '', category: '', subCategory: '',
  brand: '', model: '',
  vendor: '', poNumber: '', purchaseDate: '', purchasePrice: '', purchasePriceVat: '',
  warrantyStart: '', warrantyEnd: '', warrantyLifetime: false, location: '',
  accessories: [] as { name: string; serialNumber?: string; note?: string }[],
}

// ─── Import Modal ─────────────────────────────────────────────────────────────

function ImportModal({ opened, onClose, onDone, onDownloadTemplate }: {
  opened: boolean; onClose: () => void; onDone: () => void; onDownloadTemplate: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)

  const reset = () => { setFile(null); setResult(null) }

  const handleImport = async () => {
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/assets/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      setResult(res.data)
      if (res.data.imported > 0) onDone()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'นำเข้าไฟล์ไม่สำเร็จ' })
    } finally { setUploading(false) }
  }

  return (
    <Modal opened={opened} onClose={() => { reset(); onClose() }}
      title={<Group gap="xs"><IconFileSpreadsheet size={20} /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>นำเข้า</Text></Group>}
      size="md" radius="lg">
      <Stack gap="md">
        <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm" fw={600} mb={4}>ต้องกรอก: name, serialNumber, departmentCode*, category*</Text>
          <Text size="xs" c="dimmed">* ต้องกรอก departmentCode + category เมื่อไม่ได้ระบุ assetCode (เพื่อสร้างรหัสอัตโนมัติ)</Text>
          <Text size="xs" c="dimmed" mt={4}>ไม่บังคับ: assetCode, subCategory, brand, model, vendor, poNumber, purchaseDate (YYYY-MM-DD), purchasePrice, warrantyStart, warrantyEnd, warrantyLifetime (TRUE/FALSE), location</Text>
        </Alert>
        <Button variant="light" size="xs" color="teal" leftSection={<IconDownload size={14} />} onClick={onDownloadTemplate}>
          ดาวน์โหลดเทมเพลต (.xlsx พร้อมชีต REF)
        </Button>
        <Divider />
        <FileButton onChange={setFile} accept=".csv,.xlsx,.xls">
          {(props) => (
            <Button {...props} variant="outline" leftSection={<IconFileSpreadsheet size={16} />} color="gray">
              {file ? file.name : 'เลือกไฟล์ (.csv / .xlsx)'}
            </Button>
          )}
        </FileButton>
        {result && (
          <Box>
            <Group gap="lg" mb="xs">
              <Box ta="center"><Text size="xl" fw={600} c="lime">{result.imported}</Text><Text size="xs" c="dimmed">นำเข้าแล้ว</Text></Box>
              <Box ta="center"><Text size="xl" fw={600} c="orange">{result.skipped}</Text><Text size="xs" c="dimmed">ข้าม</Text></Box>
            </Group>
            {result.errors.length > 0 && (
              <ScrollArea h={140}><List size="xs" spacing={2}>
                {result.errors.map((e, i) => <List.Item key={i} icon={<IconAlertCircle size={12} color="orange" />}>{e}</List.Item>)}
              </List></ScrollArea>
            )}
            {result.imported > 0 && <Alert color="teal" variant="light" icon={<IconCheck size={16} />} mt="xs">{result.imported} รายการ นำเข้าสำเร็จ</Alert>}
          </Box>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => { reset(); onClose() }}>ปิด</Button>
          <Button leftSection={<IconUpload size={16} />} disabled={!file || !!result} loading={uploading} onClick={handleImport}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>นำเข้า</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AssetsPage() {
  const { t, i18n } = useTranslation()
  const STATUS_LABEL: Record<string, string> = {
    AVAILABLE: t('assetStatus.available', 'Available'), ASSIGNED: t('assetStatus.assigned', 'Assigned'),
    BORROWED: t('assetStatus.borrowed', 'Borrowed'), INSTALLED: t('assetStatus.installed', 'Installed'),
    REPAIR: t('assetStatus.repair', 'In Repair'), DAMAGED: t('assetStatus.damaged', 'Damaged'),
    LOST: t('assetStatus.lost', 'Lost'), WRITE_OFF: t('assetStatus.writeOff', 'Written Off'),
  }
  const { roles, departments: userDepts } = useAuth()
  const { isMobile, isTablet, isSmall } = useBreakpoint()

  // Is the current user an admin-level (sees all assets)?
  const isAdminLevel = roles.includes('ADMIN') || roles.includes('ASSET_MANAGER')
  // เข้มกว่า isAdminLevel — เฉพาะ Role Admin จริงๆ เท่านั้น (ไม่รวม ASSET_MANAGER) ใช้กับปุ่ม
  // Transfer ใน bulk action bar โดยเฉพาะตามที่ตกลงกันไว้
  const isAdminOnly = roles.includes('ADMIN')

  const [assets, setAssets]       = useState<any[]>([])
  const [loading, setLoading]     = useState(true)
  const [search, setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)

  // Pagination
  const [page, setPage]           = useState(1)
  const [pageSize, setPageSize]   = useState<number>(50)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  // Admin-only: filter by dept in the table
  const [deptFilter, setDeptFilter] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [subCategoryFilter, setSubCategoryFilter] = useState<string | null>(null)
  const [locationFilter, setLocationFilter] = useState<string | null>(null)

  // ── Filter bottom sheet (มือถือ) — เดิม 5 dropdown เต็มความกว้างเรียงต่อกันยาวเหยียดบนมือถือ
  // ต้องเลื่อนผ่านหมดก่อนถึงรายการจริง ย้ายมาไว้ในแผ่นเลื่อนขึ้นจากล่างแทน กดปุ่ม "ตัวกรอง" เดียว
  // เลือกเสร็จค่อยกด "ใช้ตัวกรอง" — ระหว่างเลือกอยู่ในแผ่นยังไม่กระทบรายการจริง จนกว่าจะกด Apply
  // (ใช้ draft state แยกจาก state จริง แล้ว sync กันตอนเปิด/ตอนกด Apply เท่านั้น) ──
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [draftStatus, setDraftStatus] = useState<string | null>(null)
  const [draftCategory, setDraftCategory] = useState<string | null>(null)
  const [draftSubCategory, setDraftSubCategory] = useState<string | null>(null)
  const [draftLocation, setDraftLocation] = useState<string | null>(null)
  const [draftDept, setDraftDept] = useState<string | null>(null)
  const openFilterSheet = () => {
    setDraftStatus(statusFilter); setDraftCategory(categoryFilter)
    setDraftSubCategory(subCategoryFilter); setDraftLocation(locationFilter); setDraftDept(deptFilter)
    setFilterSheetOpen(true)
  }
  const applyFilterSheet = () => {
    setStatusFilter(draftStatus); setCategoryFilter(draftCategory)
    setSubCategoryFilter(draftSubCategory); setLocationFilter(draftLocation); setDeptFilter(draftDept)
    setFilterSheetOpen(false)
  }
  const clearFilterSheet = () => {
    setDraftStatus(null); setDraftCategory(null); setDraftSubCategory(null); setDraftLocation(null); setDraftDept(null)
  }
  const activeFilterCount = [statusFilter, categoryFilter, subCategoryFilter, locationFilter, deptFilter].filter(Boolean).length
  // ── Sort — คลิกหัวคอลัมน์เพื่อเรียง A-Z/Z-A (เรียงฝั่ง server เพราะข้อมูลแบ่งหน้า
  // ถ้าเรียงแค่ฝั่ง client จะเรียงได้แค่หน้าที่โหลดมาแล้ว ไม่ใช่ทั้งชุดข้อมูลจริง) ──
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC')
  const handleSortClick = (column: string) => {
    if (sortBy === column) {
      setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(column)
      setSortDir('ASC')
    }
  }
  const SortableTh = ({ column, children }: { column: string; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSortClick(column)}>
      <Group gap={4} wrap="nowrap">
        {children}
        {sortBy === column
          ? (sortDir === 'ASC' ? <IconChevronUp size={13} /> : <IconChevronDown size={13} />)
          : <IconArrowsSort size={13} color="#cbd5e1" />}
      </Group>
    </Table.Th>
  )

  // Reference data
  const [departments, setDepartments] = useState<any[]>([])
  const [categories, setCategories]   = useState<any[]>([])
  const [subCategories, setSubCategories] = useState<any[]>([])

  // Modal
  const [opened, { open, close }] = useDisclosure(false)
  const [editing, setEditing]     = useState<any>(null)
  const [form, setForm]           = useState({ ...EMPTY_FORM })
  const [saving, setSaving]       = useState(false)
  const [triedSave, setTriedSave] = useState(false)
  const [imageFile, setImageFile]   = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  // Duplicate check state
  const [codeCheck, setCodeCheck]   = useState<'idle'|'checking'|'ok'|'dup'>('idle')
  const [snCheck, setSnCheck]       = useState<'idle'|'checking'|'ok'|'dup'>('idle')
  const [codeLoading, setCodeLoading] = useState(false)

  // ── QuickAdd modal ──────────────────────────────────────────────────────────
  const [qaOpened, { open: openQA, close: closeQA }] = useDisclosure(false)
  const [qaType, setQAType] = useState<'brand'|'category'|'subcategory'|'vendor'|'location'|'model'>('brand')
  const [qaForm, setQAForm] = useState({ code: '', name: '', parentCategory: '' })
  const [qaSaving, setQASaving] = useState(false)

  const openQuickAdd = (type: typeof qaType, parentCat = '') => {
    setQAType(type)
    setQAForm({ code: '', name: '', parentCategory: parentCat })
    openQA()
  }

  const handleQuickAdd = async () => {
    const code = qaForm.code.trim().toUpperCase()
    const name = qaForm.name.trim()
    if (!name) { notifications.show({ color: 'red', message: 'กรุณากรอกชื่อ' }); return }
    if (['brand','category','subcategory','model','location'].includes(qaType) && !code) {
      notifications.show({ color: 'red', message: 'กรุณากรอกรหัส' }); return
    }
    if (qaType === 'subcategory' && !qaForm.parentCategory) {
      notifications.show({ color: 'red', message: 'กรุณาเลือกหมวดหมู่หลัก' }); return
    }
    if (qaType === 'model' && !qaForm.parentCategory) {
      notifications.show({ color: 'red', message: 'กรุณาเลือก Brand หลัก' }); return
    }
    setQASaving(true)
    try {
      if (qaType === 'brand') {
        await createBrand({ code, name })
        setBrands(prev => [...prev, { code, name, isActive: true }])
        setForm(p => ({ ...p, brand: code }))
        loadModels(code)
      } else if (qaType === 'category') {
        await createCategory({ code, name })
        setCategories(prev => [...prev, { code, name, isActive: true }])
        handleCategoryChange(code)
      } else if (qaType === 'subcategory') {
        await createSubCategory({ code, name, category: qaForm.parentCategory })
        setSubCategories(prev => [...prev, { code, name, category: qaForm.parentCategory, isActive: true }])
        setForm(p => ({ ...p, subCategory: code }))
      } else if (qaType === 'model') {
        const newModel = { code, name, brandCode: qaForm.parentCategory, isActive: true }
        await createModel(newModel)
        setModels(prev => [...prev, newModel])
        setAllModels(prev => [...prev, newModel])
        setForm(p => ({ ...p, model: code }))
      } else if (qaType === 'vendor') {
        await createVendor({ name })
        setVendors(prev => [...prev, { name, isActive: true }])
        setForm(p => ({ ...p, vendor: name }))
      } else if (qaType === 'location') {
        await createLocation({ code, name })
        setLocations(prev => [...prev, { code, name, isActive: true }])
        setForm(p => ({ ...p, location: name }))
      }
      notifications.show({ color: 'teal', message: `เพิ่มสำเร็จ: ${name}` })
      closeQA()
    } catch (e: any) {
      const msg = e.response?.data?.message ?? 'Error'
      notifications.show({ color: 'red', message: msg })
    } finally { setQASaving(false) }
  }

  const fetchNextCode = async (dept: string, category: string) => {
    if (!dept || !category) return
    setCodeLoading(true)
    try {
      const res = await getNextAssetCode(dept, category)
      if (res.data.code) {
        setForm(p => ({ ...p, assetCode: res.data.code }))
        setCodeCheck('idle')
      }
    } catch (_) {}
    finally { setCodeLoading(false) }
  }

  // Barcode scanner
  const [scannerOpened, { open: openScanner, close: closeScanner }] = useDisclosure(false)

  // Import
  const [importOpened, { open: openImport, close: closeImport }] = useDisclosure(false)

  // Locations
  const [locations, setLocations] = useState<any[]>([])
  const [brands, setBrands]       = useState<any[]>([])
  const [models, setModels]       = useState<any[]>([])
  const [allModels, setAllModels] = useState<any[]>([])
  const [vendors, setVendors]     = useState<any[]>([])

  // Transfer dept modal
  const [transferOpened, { open: openTransfer, close: closeTransfer }] = useDisclosure(false)
  const [transferDept, setTransferDept] = useState<string | null>(null)
  const [transferNote, setTransferNote] = useState('')
  const [transferring, setTransferring] = useState(false)

  // ── Bulk selection ────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [repairModalOpen, setRepairModalOpen] = useState(false)
  const [repairVendors, setRepairVendors] = useState<any[]>([])
  const [repairAssetsList, setRepairAssetsList] = useState<any[]>([])
  const [firstSelectedId, setFirstSelectedId] = useState<number | null>(null)
  const [repairConsumables, setRepairConsumables] = useState<any[]>([])
  const [repairAllAssets, setRepairAllAssets] = useState<any[]>([])

  // ── Quick View ────────────────────────────────────────────────────────────
  const [quickViewAsset, setQuickViewAsset] = useState<any | null>(null)
  const [quickViewOpen, setQuickViewOpen] = useState(false)

  // ── Swap Asset ────────────────────────────────────────────────────────────
  const [swapModalOpen, setSwapModalOpen] = useState(false)
  const [swapAssetA, setSwapAssetA] = useState<any | null>(null)

  const openSwapModal = (asset: any) => {
    setSwapAssetA(asset)
    setSwapModalOpen(true)
  }
  const toggleSelect = (id: number) => setSelectedIds(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleSelectAll = () => {
    if (selectedIds.size === assets.length) setSelectedIds(new Set())
    else setSelectedIds(new Set(assets.map(a => a.id)))
  }
  const clearSelection = () => setSelectedIds(new Set())

  // Location move modal
  const [locationOpened, { open: openLocation, close: closeLocation }] = useDisclosure(false)
  const [locationAsset, setLocationAsset] = useState<any>(null)
  const [newLocation, setNewLocation] = useState<string | null>(null)
  const [locationNote, setLocationNote] = useState('')
  const [movingLocation, setMovingLocation] = useState(false)

  // History modal
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false)
  const [qrAssets, setQrAssets] = useState<any[]>([])
  const [qrOpened, { open: openQR, close: closeQR }] = useDisclosure(false)
  const [assetLicenseMap, setAssetLicenseMap] = useState<Record<string, any[]>>({})
  const [historyAsset, setHistoryAsset] = useState<any>(null)
  const [historyData, setHistoryData] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Auto-generate asset code
  const [loadingCode, setLoadingCode] = useState(false)
  const [isCloning, setIsCloning]     = useState(false)

  // ─── Load ─────────────────────────────────────────────────────────────────

  const loadAssets = useCallback((overridePage?: number) => {
    setLoading(true)
    const currentPage = overridePage ?? page
    getAssetsPaginated({
      page: currentPage,
      limit: pageSize,
      status: statusFilter ?? undefined,
      departmentCode: deptFilter ?? undefined,
      category: categoryFilter ?? undefined,
      subCategory: subCategoryFilter ?? undefined,
      location: locationFilter ?? undefined,
      search: search || undefined,
      sortBy: sortBy ?? undefined,
      sortDir,
    })
      .then((r) => {
        setAssets(r.data.data)
        setTotalPages(r.data.totalPages)
        setTotalRecords(r.data.total)
        if (overridePage !== undefined) setPage(overridePage)
      })
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter, deptFilter, categoryFilter, subCategoryFilter, locationFilter, search, sortBy, sortDir])

  const loadRef = async () => {
    // Load core refs first — must not fail
    const [d, c, s, l, b, m] = await Promise.all([
      getDepartments(), getCategories(), getSubCategories(),
      getLocations(), getBrands(), getModels(),
    ])
    setDepartments(d.data.filter((x: any) => x.isActive !== false))
    setCategories(c.data.filter((x: any) => x.isActive !== false))
    setSubCategories(s.data.filter((x: any) => x.isActive !== false))
    setLocations(l.data.filter((x: any) => x.isActive !== false))
    setBrands(b.data.filter((x: any) => x.isActive !== false))
    setAllModels(m.data)
    // Vendors — graceful fallback if endpoint not yet deployed
    try {
      const v = await getVendors()
      setVendors(v.data.filter((x: any) => x.isActive !== false))
    } catch (_) { /* vendor endpoint may not exist yet */ }
    // License map — graceful fallback
    try {
      const licMap = await getAllAssetLicenseMap()
      setAssetLicenseMap(licMap.data ?? {})
    } catch (_) {}
  }

  // Load models filtered by brand
  const loadModels = async (brandCode?: string) => {
    if (!brandCode) { setModels([]); return }
    const res = await getModels(brandCode)
    setModels(res.data.filter((x: any) => x.isActive !== false))
  }

  // Initial load only
  useEffect(() => { loadRef() }, [])

  // ── Auto-open Add form from ?action=add&serial=XX (from Scan page) ────────
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [highlightCode, setHighlightCode] = useState<string | null>(null)

  useEffect(() => {
    if (searchParams.get('action') === 'add') {
      const serial = searchParams.get('serial') ?? ''
      const defaultDept = !isAdminLevel && userDepts.length === 1 ? userDepts[0] : ''
      setEditing(null)
      setIsCloning(false)
      setForm({ ...EMPTY_FORM, serialNumber: serial, departmentCode: defaultDept })
      setImageFile(null); setImagePreview(null)
      setCodeCheck('idle'); setSnCheck('idle'); setTriedSave(false)
      open()
      setSearchParams({})
    }
    // highlight จาก Scan page
    if (searchParams.get('highlight')) {
      const code = searchParams.get('highlight')!
      setHighlightCode(code)
      setSearch(code)
      setSearchParams({})
      // ลบ highlight หลัง 3 วินาที
      setTimeout(() => setHighlightCode(null), 3000)
    }
    // deep link จากการแชร์ — lookup asset แล้ว redirect ไปหน้าที่เหมาะสมตาม status
    if (searchParams.get('asset')) {
      const code = searchParams.get('asset')!
      setSearchParams({})
      const force = searchParams.get('force') === '1'
      scanLookupAsset(code).then(res => {
        if (!res.data?.found) return
        const a = res.data
        const status = a.status ?? ''
        if (!force && status === 'REPAIR') {
          // ไปหน้า Repair พร้อม filter และ All Jobs view
          navigate(`/maintenance?assetCode=${encodeURIComponent(a.assetCode)}`)
        } else if (!force && (status === 'DAMAGED' || status === 'WRITE_OFF')) {
          // ไปหน้า Disposal (Write-Off page)
          navigate('/write-off')
        } else {
          // force=1 (ผู้ใช้กด Open in Assets page เอง) หรือ AVAILABLE, ASSIGNED, BORROWED, LOST — เปิด QuickView ตามปกติ
          setQuickViewAsset(a)
          setQuickViewOpen(true)
        }
      }).catch(() => {})
    }
  }, [searchParams])

  // Load assets when page/pageSize changes (including initial)
  useEffect(() => { loadAssets() }, [page, pageSize])

  // Reset to page 1 and reload when filters change
  useEffect(() => {
    setPage(1)
    loadAssets(1)
  }, [statusFilter, deptFilter, categoryFilter, subCategoryFilter, locationFilter, sortBy, sortDir])

  // Debounce search — reset page and reload
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); loadAssets(1) }, 400)
    return () => clearTimeout(t)
  }, [search])

  // ── Real-time duplicate check — Asset Code ────────────────────────────────
  useEffect(() => {
    if (!opened) return
    const code = form.assetCode?.trim()
    if (!code) { setCodeCheck('idle'); return }
    // Skip check when editing and code hasn't changed
    if (editing && editing.assetCode === code) { setCodeCheck('ok'); return }
    setCodeCheck('checking')
    const t = setTimeout(async () => {
      try {
        const res = await checkAssetDuplicate({
          assetCode: code,
          excludeId: editing?.id,
        })
        setCodeCheck(res.data.exists ? 'dup' : 'ok')
      } catch { setCodeCheck('idle') }
    }, 500)
    return () => clearTimeout(t)
  }, [form.assetCode, opened])

  // ── Real-time duplicate check — Serial Number ─────────────────────────────
  useEffect(() => {
    if (!opened) return
    const sn = form.serialNumber?.trim()
    if (!sn) { setSnCheck('idle'); return }
    // Skip check when editing and SN hasn't changed
    if (editing && editing.serialNumber === sn) { setSnCheck('ok'); return }
    setSnCheck('checking')
    const t = setTimeout(async () => {
      try {
        const res = await checkAssetDuplicate({
          serialNumber: sn,
          excludeId: editing?.id,
        })
        setSnCheck(res.data.exists ? 'dup' : 'ok')
      } catch { setSnCheck('idle') }
    }, 500)
    return () => clearTimeout(t)
  }, [form.serialNumber, opened])

  // ─── Computed ──────────────────────────────────────────────────────────────

  const deptData = departments.map((d) => ({ value: d.code, label: `${d.name}  (${d.code})` }))
  const catData  = categories.map((c) => ({ value: c.code, label: `${localizedName(c, i18n.language)}  (${c.code})` }))
  const subCatData = subCategories
    .filter((s) => !form.category || s.category === form.category)
    .map((s) => ({ value: s.code, label: `${s.name}  (${s.code})` }))
  // ── สำหรับ filter bar แยกจาก subCatData ของฟอร์ม (นั่นอิงตาม form.category ตอนสร้าง/แก้ asset)
  // ตัวนี้อิงตาม categoryFilter แทน — ไม่เลือก Category เลย = โชว์ครบทุก sub-category ตามที่ตกลงไว้
  const subCatFilterData = subCategories
    .filter((s) => !categoryFilter || s.category === categoryFilter)
    .map((s) => ({ value: s.code, label: `${s.name}  (${s.code})` }))
  // เหมือนกันแต่อิงตาม draftCategory — ให้แผ่นตัวกรองบนมือถือ cascading ได้เองระหว่างที่ยังไม่กด Apply
  const draftSubCatData = subCategories
    .filter((s) => !draftCategory || s.category === draftCategory)
    .map((s) => ({ value: s.code, label: `${s.name}  (${s.code})` }))

  // Server-side filtering — assets from API are already filtered
  const filtered = assets

  // ─── Open modals ──────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditing(null)
    setIsCloning(false)
    const defaultDept = !isAdminLevel && userDepts.length === 1 ? userDepts[0] : ''
    setForm({ ...EMPTY_FORM, departmentCode: defaultDept })
    setImageFile(null); setImagePreview(null)
    setCodeCheck('idle'); setSnCheck('idle'); setTriedSave(false)
    open()
    // ถ้ามี dept อยู่แล้ว รอ category ก่อน (จะ fetch เมื่อ select category)
  }

  const openEdit = (asset: any) => {
    setEditing(asset)
    setForm({
      assetCode:     asset.assetCode ?? '',
      name:          asset.name ?? '',
      serialNumber:  asset.serialNumber ?? '',
      departmentCode:asset.departmentCode ?? '',
      category:      asset.category ?? '',
      subCategory:   asset.subCategory ?? '',
      brand:         asset.brand ?? '',
      model:         asset.model ?? '',
      vendor:        asset.vendor ?? '',
      poNumber:      asset.poNumber ?? '',
      purchaseDate:  asset.purchaseDate ? asset.purchaseDate.slice(0, 10) : '',
      purchasePrice: asset.purchasePrice ?? '',
      purchasePriceVat: asset.purchasePriceVat ?? '',
      warrantyStart:    asset.warrantyStart ? asset.warrantyStart.slice(0, 10) : '',
      warrantyEnd:      asset.warrantyEnd   ? asset.warrantyEnd.slice(0, 10)   : '',
      warrantyLifetime: asset.warrantyLifetime ?? false,
      location:         asset.location ?? '',
      accessories:      asset.accessories ?? [],
    })
    setImageFile(null); setImagePreview(asset.imageUrl ?? null)
    if (asset.brand) loadModels(asset.brand)
    setCodeCheck('idle'); setSnCheck('idle'); setTriedSave(false)
    open()
  }

  const openClone = async (asset: any) => {
    setEditing(null)
    setIsCloning(true)
    // Auto-generate next code from same dept + category
    let nextCode = ''
    if (asset.departmentCode && asset.category) {
      try {
        const res = await getNextAssetCode(asset.departmentCode, asset.category)
        nextCode = res.data.code
      } catch (_) {}
    }
    setForm({
      assetCode:     nextCode,
      name:          '',           // บังคับกรอกใหม่
      serialNumber:  '',           // บังคับกรอกใหม่
      departmentCode:asset.departmentCode ?? '',
      category:      asset.category ?? '',
      subCategory:   asset.subCategory ?? '',
      brand:         asset.brand ?? '',
      model:         asset.model ?? '',
      vendor:        asset.vendor ?? '',
      poNumber:      '',
      purchaseDate:  '',
      purchasePrice: asset.purchasePrice ?? '',
      purchasePriceVat: asset.purchasePriceVat ?? '',
      warrantyStart: '',
      warrantyEnd:   '',
      warrantyLifetime: false,
      location:      asset.location ?? '',
      accessories:   asset.accessories ?? [],
    })
    if (asset.brand) loadModels(asset.brand)
    setImageFile(null); setImagePreview(null)
    open()
  }

  const handleCategoryChange = (val: string | null) => {
    setForm((p) => ({ ...p, category: val ?? '', subCategory: '' }))
    // Auto-generate asset code เมื่อเลือก category (เฉพาะตอน create ใหม่)
    if (!editing && val && form.departmentCode) {
      fetchNextCode(form.departmentCode, val)
    }
  }

  const handleBrandChange = (val: string | null) => {
    setForm((p) => ({ ...p, brand: val ?? '', model: '' }))
    loadModels(val ?? undefined)
  }

  const handleImagePick = (file: File | null) => {
    setImageFile(file)
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target?.result as string)
      reader.readAsDataURL(file)
    } else { setImagePreview(editing?.imageUrl ?? null) }
  }

  // ─── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setTriedSave(true)
    if (!form.assetCode || !form.name || !form.serialNumber || !form.category) {
      notifications.show({
        color: 'red',
        message: !form.category
          ? 'กรุณาเลือก Category ก่อน'
          : t('assets.requiredFields','Asset Code, Name and Serial Number are required'),
      })
      return
    }
    setSaving(true)
    try {
      const payload = {
        ...form,
        purchasePrice:    form.purchasePrice    ? Number(form.purchasePrice)    : undefined,
        purchasePriceVat: form.purchasePriceVat ? Number(form.purchasePriceVat) : undefined,
        purchaseDate:  form.purchaseDate  || undefined,
        warrantyStart: form.warrantyStart || undefined,
        warrantyEnd:   form.warrantyLifetime ? undefined : (form.warrantyEnd || undefined),
        warrantyLifetime: form.warrantyLifetime,
      }
      let savedId: number
      if (editing) {
        await updateAsset(editing.id, payload)
        savedId = editing.id
        notifications.show({ color: 'lime', message: 'อัปเดตทรัพย์สินเรียบร้อย' })
      } else {
        const res = await createAsset(payload)
        savedId = res.data.id
        notifications.show({ color: 'lime', message: 'เพิ่มทรัพย์สินเรียบร้อย' })
      }
      if (imageFile && savedId) await uploadAssetImage(savedId, imageFile)
      close(); loadAssets(page)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'เกิดข้อผิดพลาด' })
    } finally { setSaving(false) }
  }

  const handleDelete = (asset: any) => {
    modals.openConfirmModal({
      title: 'ตัดจำหน่ายทรัพย์สิน',
      children: <Text size="sm">ตัดจำหน่าย <b>{asset.name}</b>? สถานะจะเปลี่ยนเป็น <b>{t('assets.writeOff', 'ตัดจำหน่าย')}</b></Text>,
      labels: { confirm: 'ตัดจำหน่าย', cancel: 'ยกเลิก' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await deleteAsset(asset.id)
        notifications.show({ color: 'lime', message: 'ตัดจำหน่ายทรัพย์สินแล้ว' })
        loadAssets(page)
      },
    })
  }

  const handleHardDelete = (asset: any) => {
    modals.openConfirmModal({
      title: '⚠️ ลบทรัพย์สินถาวร',
      children: (
        <Stack gap="xs">
          <Text size="sm">ลบ <b>{asset.name}</b> ({asset.assetCode}) ออกจากระบบถาวร?</Text>
          <Text size="xs" c="red">ข้อมูลทั้งหมดจะถูกลบและไม่สามารถกู้คืนได้ เฉพาะ ADMIN เท่านั้น</Text>
        </Stack>
      ),
      labels: { confirm: 'ลบถาวร', cancel: 'ยกเลิก' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await hardDeleteAsset(asset.id)
          notifications.show({ color: 'lime', message: `ลบ ${asset.name} ถาวรแล้ว` })
          loadAssets(page)
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? 'ลบไม่สำเร็จ' })
        }
      },
    })
  }

  // ─── Lost / Damaged ────────────────────────────────────────────────────────
  const [lostAsset, setLostAsset]         = useState<any>(null)
  const [lostOpened, { open: openLost, close: closeLost }] = useDisclosure(false)
  const [lostForm, setLostForm]           = useState({ lostDate: '', lostLocation: '', lostNote: '' })
  const [lostIncidentFile, setLostIncidentFile] = useState<File | null>(null)
  const [lostSaving, setLostSaving]       = useState(false)

  const [foundAsset, setFoundAsset]       = useState<any>(null)
  const [foundOpened, { open: openFound, close: closeFound }] = useDisclosure(false)
  const [foundCondition, setFoundCondition] = useState<string>('GOOD')
  const [foundNote, setFoundNote]         = useState('')
  const [foundSaving, setFoundSaving]     = useState(false)

  const [damagedAsset, setDamagedAsset]   = useState<any>(null)
  const [damagedOpened, { open: openDamaged, close: closeDamaged }] = useDisclosure(false)
  const [damagedForm, setDamagedForm]     = useState({ damagedDate: '', damagedNote: '' })
  const [damagedIncidentFile, setDamagedIncidentFile] = useState<File | null>(null)
  const [damagedSaving, setDamagedSaving] = useState(false)

  // ─── Install / Uninstall ───────────────────────────────────────────────────
  const [installAsset, setInstallAsset]   = useState<any>(null)
  const [installOpened, { open: openInstall, close: closeInstall }] = useDisclosure(false)
  const [installForm, setInstallForm]     = useState({ location: '', installedDate: '', note: '' })
  const [installSaving, setInstallSaving] = useState(false)
  const [uninstallSaving, setUninstallSaving] = useState(false)

  const handleOpenInstall = (asset: any) => {
    setInstallAsset(asset)
    setInstallForm({ location: asset.location ?? '', installedDate: '', note: '' })
    openInstall()
  }

  const handleUninstall = (asset: any) => {
    modals.openConfirmModal({
      title: t('assets.uninstallConfirmTitle', 'Uninstall this asset?'),
      children: <Text size="sm">{t('assets.uninstallConfirmBody', 'This will move the asset back to Available status.')}</Text>,
      labels: { confirm: t('assets.uninstallBtn', 'Uninstall'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'gray' },
      onConfirm: async () => {
        setUninstallSaving(true)
        try {
          await uninstallAsset(asset.id)
          notifications.show({ color: 'teal', message: t('assets.uninstallSuccess', 'Asset uninstalled') })
          loadAssets()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
        } finally { setUninstallSaving(false) }
      },
    })
  }

  const handleSubmitInstall = async () => {
    if (!installAsset || !installForm.location.trim()) {
      notifications.show({ color: 'red', message: t('assets.installLocationRequired', 'Location is required') })
      return
    }
    setInstallSaving(true)
    try {
      await markAssetInstalled(installAsset.id, {
        location: installForm.location.trim(),
        installedDate: installForm.installedDate || undefined,
        note: installForm.note || undefined,
      })
      notifications.show({ color: 'teal', message: t('assets.installSuccess', 'Asset marked as Installed') })
      closeInstall(); loadAssets()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setInstallSaving(false) }
  }

  const handleOpenLost = (asset: any) => {
    setLostAsset(asset)
    setLostForm({ lostDate: '', lostLocation: '', lostNote: '' })
    setLostIncidentFile(null)
    openLost()
  }
  const handleOpenFound = (asset: any) => {
    setFoundAsset(asset)
    setFoundCondition('GOOD')
    setFoundNote('')
    openFound()
  }
  const handleOpenDamaged = (asset: any) => {
    setDamagedAsset(asset)
    setDamagedForm({ damagedDate: '', damagedNote: '' })
    setDamagedIncidentFile(null)
    openDamaged()
  }

  const handleSubmitLost = async () => {
    if (!lostAsset) return
    setLostSaving(true)
    try {
      const res = await reportAssetLost(lostAsset.id, lostForm)
      if (lostIncidentFile) {
        await uploadLostIncident(res.data.id ?? lostAsset.id, lostIncidentFile).catch(() => {})
      }
      notifications.show({ color: 'orange', message: `Reported "${lostAsset.name}" as lost` })
      closeLost(); loadAssets(page)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setLostSaving(false) }
  }

  const handleSubmitFound = async () => {
    if (!foundAsset) return
    setFoundSaving(true)
    try {
      await markAssetFound(foundAsset.id, { condition: foundCondition, note: foundNote })
      notifications.show({ color: 'teal', message: `"${foundAsset.name}" marked as found — status updated` })
      closeFound(); loadAssets(page)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setFoundSaving(false) }
  }

  const handleSubmitDamaged = async () => {
    if (!damagedAsset) return
    setDamagedSaving(true)
    try {
      const res = await reportAssetDamaged(damagedAsset.id, damagedForm)
      if (damagedIncidentFile) {
        await uploadDamagedIncident(res.data.id ?? damagedAsset.id, damagedIncidentFile).catch(() => {})
      }
      notifications.show({ color: 'red', message: `Reported "${damagedAsset.name}" as damaged` })
      closeDamaged(); loadAssets(page)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setDamagedSaving(false) }
  }

  // ─── Bulk Transfer ────────────────────────────────────────────────────────
  const openBulkTransfer = () => {
    setTransferDept(null)
    setTransferNote('')
    openTransfer()
  }

  const handleTransfer = async () => {
    if (!transferDept) { notifications.show({ color: 'red', message: 'กรุณาเลือกแผนกปลายทาง' }); return }
    if (selectedIds.size === 0) { notifications.show({ color: 'red', message: 'กรุณาเลือกทรัพย์สินอย่างน้อย 1 ชิ้น' }); return }
    setTransferring(true)
    try {
      await transferDepartment({ assetIds: Array.from(selectedIds), toDepartmentCode: transferDept, note: transferNote || undefined })
      notifications.show({ color: 'lime', message: `Transferred ${selectedIds.size} asset(s) to ${transferDept}` })
      closeTransfer(); clearSelection(); loadAssets(page)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'โอนย้ายไม่สำเร็จ' })
    } finally { setTransferring(false) }
  }

  // ─── Bulk Mark as Installed — ใช้ location เดิมของแต่ละชิ้น ไม่ต้องกรอกใหม่ ───────
  const [bulkInstalling, setBulkInstalling] = useState(false)
  const handleBulkMarkInstalled = async () => {
    const selected = assets.filter(a => selectedIds.has(a.id))
    const notAvailable = selected.filter(a => a.status !== 'AVAILABLE')
    const noLocation = selected.filter(a => a.status === 'AVAILABLE' && !a.location?.trim())
    if (notAvailable.length > 0) {
      notifications.show({ color: 'red', message: t('assets.bulkInstallNotAvailable', '{{count}} item(s) are not AVAILABLE — only AVAILABLE assets can be marked as Installed', { count: notAvailable.length }) })
      return
    }
    if (noLocation.length > 0) {
      notifications.show({ color: 'red', message: t('assets.bulkInstallNoLocation', '{{count}} item(s) have no Location set yet — set a location first, or install them one at a time', { count: noLocation.length }) })
      return
    }
    setBulkInstalling(true)
    try {
      const results = await Promise.allSettled(
        selected.map(a => markAssetInstalled(a.id, { location: a.location }))
      )
      const failCount = results.filter(r => r.status === 'rejected').length
      const okCount = results.length - failCount
      if (failCount > 0) {
        notifications.show({ color: 'orange', message: t('assets.bulkInstallPartial', '{{ok}} installed, {{fail}} failed', { ok: okCount, fail: failCount }) })
      } else {
        notifications.show({ color: 'lime', message: t('assets.bulkInstallSuccess', 'Marked {{count}} asset(s) as Installed', { count: okCount }) })
      }
      clearSelection(); loadAssets(page)
    } finally { setBulkInstalling(false) }
  }

  // ─── Bulk Repair ──────────────────────────────────────────────────────────
  const handleBulkMaintenance = async () => {
    if (selectedIds.size === 0) return
    const selectedAssets = assets.filter(a => selectedIds.has(a.id))
    const repairReady = selectedAssets.map(a => ({ ...a, status: 'REPAIR' }))
    setRepairAssetsList(repairReady)
    setFirstSelectedId(selectedAssets[0]?.id ?? null)
    try {
      const [v, cons, allA] = await Promise.all([
        repairVendors.length === 0 ? getVendors() : Promise.resolve({ data: repairVendors }),
        repairConsumables.length === 0 ? getInventoryItems() : Promise.resolve({ data: repairConsumables }),
        repairAllAssets.length === 0 ? getAllAssetsAdmin() : Promise.resolve({ data: repairAllAssets }),
      ])
      setRepairVendors(v.data ?? [])
      setRepairConsumables((cons.data ?? []).filter((x: any) => x.isActive !== false && (x.stockQty ?? x.currentStock ?? 0) > 0))
      setRepairAllAssets(allA.data ?? [])
    } catch {}
    setRepairModalOpen(true)
  }

  const openRepairForAsset = async (asset: any) => {
    setRepairAssetsList([asset])
    setFirstSelectedId(asset.id)
    try {
      const [v, cons, allA] = await Promise.all([
        repairVendors.length === 0 ? getVendors() : Promise.resolve({ data: repairVendors }),
        repairConsumables.length === 0 ? getInventoryItems() : Promise.resolve({ data: repairConsumables }),
        repairAllAssets.length === 0 ? getAllAssetsAdmin() : Promise.resolve({ data: repairAllAssets }),
      ])
      setRepairVendors(v.data ?? [])
      setRepairConsumables((cons.data ?? []).filter((x: any) => x.isActive !== false && (x.stockQty ?? x.currentStock ?? 0) > 0))
      setRepairAllAssets(allA.data ?? [])
    } catch {}
    setRepairModalOpen(true)
  }

  // ─── Bulk Delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return
    modals.openConfirmModal({
      title: 'ตัดจำหน่ายทรัพย์สินที่เลือก',
      children: <Text size="sm">ตัดจำหน่าย <b>{selectedIds.size} รายการ</b>? สถานะจะเปลี่ยนเป็น <b>{t('assets.writeOff', 'ตัดจำหน่าย')}</b></Text>,
      labels: { confirm: 'ตัดจำหน่ายทั้งหมด', cancel: 'ยกเลิก' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await Promise.all(Array.from(selectedIds).map(id => deleteAsset(id)))
          notifications.show({ color: 'lime', message: `ตัดจำหน่ายแล้ว ${selectedIds.size} รายการ` })
          clearSelection(); loadAssets(page)
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
        }
      },
    })
  }

  // ─── Location Move ─────────────────────────────────────────────────────────
  const handleOpenLocation = (asset: any) => {
    setLocationAsset(asset)
    setNewLocation(null)
    setLocationNote('')
    openLocation()
  }

  const handleMoveLocation = async () => {
    if (!newLocation) { notifications.show({ color: 'red', message: 'กรุณาเลือกสถานที่' }); return }
    setMovingLocation(true)
    try {
      await moveLocation({ assetId: locationAsset.id, toLocation: newLocation, note: locationNote || undefined })
      notifications.show({ color: 'lime', message: 'อัปเดตสถานที่เรียบร้อย' })
      closeLocation(); loadAssets(page)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'ย้ายสถานที่ไม่สำเร็จ' })
    } finally { setMovingLocation(false) }
  }

  // ─── Asset History ─────────────────────────────────────────────────────────
  const handleOpenHistory = async (asset: any) => {
    setHistoryAsset(asset)
    setHistoryData([])
    openHistory()
    setLoadingHistory(true)
    try {
      const [histRes, assetRes] = await Promise.all([
        getAssetHistory(asset.id),
        getAsset(asset.id),
      ])
      setHistoryData(histRes.data)
      setHistoryAsset(assetRes.data)   // override with full data incl. incidentUrls
    } catch (_) {} finally { setLoadingHistory(false) }
  }

  const f = (k: string) => (v: any) => setForm((p) => ({ ...p, [k]: v }))

  // ─── Render ────────────────────────────────────────────────────────────────

  const downloadImportTemplate = async () => {
    try {
      const ref = await getImportReference()
      const { departments, categories, subCategories, brands, vendors, locations } = ref.data

      const wb = XLSX.utils.book_new()

      // ── Main import sheet ──────────────────────────────────────────────────
      const headers = [
        'assetCode','name','serialNumber','departmentCode','category','subCategory',
        'brand','model','vendor','location','poNumber',
        'purchasePrice','purchaseDate','warrantyStart','warrantyEnd','warrantyLifetime',
        'notes',
      ]
      const exampleRow = [
        '(auto)','Laptop Dell','SN12345','IT','LAPTOP','NOTEBOOK',
        'DELL','Latitude 5540','SD PHUKET','1201','PO-2024-001',
        '45000','2024-01-15','2024-01-15','2027-01-15','FALSE',
        'optional notes',
      ]
      const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow])

      // Column widths
      ws['!cols'] = headers.map((h) => ({ wch: h === 'name' ? 30 : h === 'notes' ? 40 : 18 }))

      // ── Add data validation dropdowns via named ranges workaround ──────────
      // Create reference lists as hidden sheets
      const refData: Record<string, string[][]> = {
        'REF_Departments': [['Code (use this)','Name'], ...departments.map((d: any) => [d.code, d.name])],
        'REF_Categories':  [['Code (use this)','Name'], ...categories.map((c: any) => [c.code, c.name])],
        'REF_SubCats':     [['Code (use this)','Name'], ...subCategories.map((s: any) => [s.code, s.name])],
        'REF_Brands':      [['Code (use this)','Name'], ...brands.map((b: any) => [b.code, b.name])],
        'REF_Vendors':     [['Name (use this)'], ...vendors.map((v: any) => [v.name])],
        'REF_Locations':   [['Name (use this)'], ...locations.map((l: any) => [l.name])],
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Import Assets')

      Object.entries(refData).forEach(([sheetName, data]) => {
        const refWs = XLSX.utils.aoa_to_sheet(data)
        refWs['!cols'] = [{ wch: 25 }, { wch: 35 }]
        XLSX.utils.book_append_sheet(wb, refWs, sheetName)
      })

      // Write file
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `AssetOS_Import_Template_${new Date().toISOString().slice(0,10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      notifications.show({ color: 'teal', message: 'ดาวน์โหลดเทมเพลตแล้ว — ดูค่าที่ใช้ได้ในชีต REF_*' })
    } catch {
      notifications.show({ color: 'red', message: 'ดาวน์โหลดเทมเพลตไม่สำเร็จ' })
    }
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Box>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
            {t('assets.title', 'Assets')}
          </Text>
          <Text size="sm" c="dimmed">
            {isAdminLevel
              ? t('assets.subtitleAll','All registered assets across all departments')
              : `${t('assets.inYourDept','Assets in your department')}${userDepts.length > 1 ? 's' : ''}: ${userDepts.map(code => departments.find(d => d.code === code)?.name ?? code).join(', ')}`
            }
          </Text>
        </Box>
        <Group gap="sm">
          {selectedIds.size > 0 && (
            <>
              {isAdminOnly && (
                <Button variant="light" color="blue"
                  leftSection={<IconArrowsTransferUp size={15} />}
                  onClick={openBulkTransfer}>
                  {t('assets.bulkTransfer', 'Transfer')} ({selectedIds.size})
                </Button>
              )}
              <Button variant="light" color="cyan"
                leftSection={<IconPlugConnected size={15} />}
                loading={bulkInstalling}
                onClick={handleBulkMarkInstalled}>
                {t('assets.bulkMarkInstalled', 'Mark Installed')} ({selectedIds.size})
              </Button>
              <Button variant="light" color="teal"
                leftSection={<IconQrcode size={15} />}
                onClick={() => {
                  const sel = assets.filter(a => selectedIds.has(a.id))
                  setQrAssets(sel)
                  openQR()
                }}>
                {t('assets.bulkPrintQR', 'Print QR')} ({selectedIds.size})
              </Button>
              <Button variant="subtle" color="gray" size="sm" px={8} onClick={clearSelection}>
                ✕ {t('assets.bulkSelectedCount', '{{count}} selected', { count: selectedIds.size })}
              </Button>
            </>
          )}
          {isAdminLevel && (
            <>
              <Button variant="outline" leftSection={<IconFileSpreadsheet size={16} />} onClick={openImport} color="gray">
                {t('assets.importCSV','Import CSV / Excel')}
              </Button>
            </>
          )}
          <Button variant="outline" color="teal" leftSection={<IconDownload size={16} />}
            onClick={async () => {
              try {
                // Export เฉพาะ assets ที่ filter ไว้ปัจจุบัน ถ้าไม่มี filter → export ทั้งหมด
                let exportAssets: any[] = assets
                const hasFilter = statusFilter || deptFilter || categoryFilter || subCategoryFilter || search
                if (!hasFilter) {
                  const res = await getAllAssetsAdmin()
                  exportAssets = res.data ?? []
                }
                const cols = [
                  { key: 'assetCode', label: 'Asset Code' }, { key: 'name', label: 'Name' },
                  { key: 'serialNumber', label: 'Serial No.' }, { key: 'departmentCode', label: 'Department' },
                  { key: 'category', label: 'Category' }, { key: 'subCategory', label: 'Sub Category' },
                  { key: 'brand', label: 'Brand' }, { key: 'model', label: 'Model' },
                  { key: 'status', label: 'Status' }, { key: 'location', label: 'Location' },
                  { key: 'vendor', label: 'Vendor' }, { key: 'poNumber', label: 'PO Number' },
                  { key: 'purchasePrice', label: 'Purchase Price' },
                  { key: 'purchaseDate', label: t('assets.labelPurchaseDate','Purchase Date') },
                  { key: 'warrantyStart', label: t('assets.labelWarrantyStart','Warranty Start') },
                  { key: 'warrantyEnd', label: t('assets.labelWarrantyEnd','Warranty End') },
                  { key: 'warrantyLifetime', label: t('assets.labelWarrantyLifetime','Warranty Lifetime') },
                  { key: 'notes', label: 'Notes' },
                ]
                const header = cols.map(c => `"${c.label}"`).join(',')
                const body = exportAssets.map(row =>
                  cols.map(c => `"${String(row[c.key] ?? '').replace(/"/g, '""')}"`).join(',')
                ).join('\n')
                const blob = new Blob(['\uFEFF' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `assets-${hasFilter ? 'filtered-' : 'all-'}${new Date().toISOString().slice(0,10)}.csv`
                a.click()
                URL.revokeObjectURL(url)
                notifications.show({ color: 'teal', message: `Export ${exportAssets.length} assets เรียบร้อย${hasFilter ? ' (filtered)' : ''}` })
              } catch {
                notifications.show({ color: 'red', message: 'Export ไม่สำเร็จ กรุณาลองใหม่' })
              }
            }}>
            {t('assets.exportCSV','Export CSV')} {(statusFilter || deptFilter || categoryFilter || subCategoryFilter || search) ? `(${assets.length})` : `(${t('common.all', 'All')})`}
          </Button>
          <Button leftSection={<IconPlus size={16} />} onClick={openAdd}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
            {t('assets.addAsset','Add Asset')}
          </Button>
        </Group>
      </Group>

      {/* Dept visibility info for non-admins */}
      {!isAdminLevel && userDepts.length > 0 && (
        <Alert icon={<IconBuilding size={16} />} color="teal" variant="light" mb="md" radius="lg">
          <Text size="sm">
            {t('assets.showingFrom','Showing assets from')} <b>{userDepts.length}</b> {t('assets.department','department')}{userDepts.length > 1 ? 's' : ''}:{' '}
            {userDepts.map((code) => {
              const d = departments.find((x) => x.code === code)
              return <Badge key={code} size="sm" color="teal" variant="light" mr={4}>{d?.name ?? code}</Badge>
            })}
          </Text>
        </Alert>
      )}

      {/* Filters */}
      <Group mb="lg" gap="sm">
        <TextInput
          placeholder={t("assets.searchPlaceholder","Search by asset code, name, serial...")}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: isMobile ? undefined : 300, width: isMobile ? '100%' : undefined }}
        />
        {isMobile ? (
          // ── มือถือ: ปุ่มเดียวเปิดแผ่นตัวกรองเลื่อนขึ้นจากล่าง แทน 5 dropdown เต็มความกว้างเรียง
          // ต่อกันยาวเหยียด — ต้องเลื่อนผ่านให้หมดก่อนถึงรายการจริงเดิม ──
          <Indicator label={activeFilterCount || undefined} disabled={activeFilterCount === 0} size={16} color="teal">
            <Button variant="default" leftSection={<IconFilter size={15} />} onClick={openFilterSheet}>
              {t('assets.filterBtn', 'Filter')}
            </Button>
          </Indicator>
        ) : (<>
        <Select
          placeholder={t("assets.allStatuses","All statuses")}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          data={[
            { value: 'AVAILABLE',  label: STATUS_LABEL.AVAILABLE },
            { value: 'ASSIGNED',   label: STATUS_LABEL.ASSIGNED },
            { value: 'BORROWED',   label: STATUS_LABEL.BORROWED },
            { value: 'INSTALLED',  label: STATUS_LABEL.INSTALLED },
            { value: 'REPAIR',     label: STATUS_LABEL.REPAIR },
            { value: 'DAMAGED',    label: STATUS_LABEL.DAMAGED },
            { value: 'LOST',       label: STATUS_LABEL.LOST },
            { value: 'WRITE_OFF',  label: STATUS_LABEL.WRITE_OFF },
          ]}
          style={{ width: 160 }}
        />
        {/* Category filter */}
        <Select
          placeholder={t("assets.allCategories","All categories")}
          clearable
          searchable
          value={categoryFilter}
          onChange={(v) => {
            setCategoryFilter(v)
            // เปลี่ยน Category แล้ว sub-category เดิมอาจไม่อยู่ในกลุ่มใหม่แล้ว — เคลียร์ทิ้งกัน
            // ค้างค่าที่ไม่ตรงกับ Category ที่เพิ่งเลือก (เหมือน pattern เดียวกับในฟอร์มสร้าง/แก้ asset)
            if (v && !subCategories.some((s: any) => s.category === v && s.code === subCategoryFilter)) {
              setSubCategoryFilter(null)
            }
          }}
          data={categories.map((c: any) => ({ value: c.code, label: localizedName(c, i18n.language) || c.code }))}
          style={{ width: 180 }}
        />
        {/* Sub Category filter — อิงตาม Category ที่เลือกก่อนเสมอ ไม่เลือก Category = โชว์ทั้งหมด */}
        <Select
          placeholder={t("assets.allSubCategories","All sub-categories")}
          clearable
          searchable
          value={subCategoryFilter}
          onChange={setSubCategoryFilter}
          data={subCatFilterData}
          style={{ width: 180 }}
        />
        {/* Location filter */}
        <Select
          placeholder={t("assets.allLocations","All locations")}
          clearable
          searchable
          leftSection={<IconMapPin size={14} />}
          value={locationFilter}
          onChange={setLocationFilter}
          data={locations.map((l: any) => ({ value: l.name, label: localizedName(l, i18n.language) }))}
          style={{ width: 180 }}
        />
        {/* Department filter — admin only */}
        {isAdminLevel && (
          <Select
            placeholder={t("common.allDept","All departments")} clearable
            leftSection={<IconBuilding size={14} />}
            value={deptFilter} onChange={setDeptFilter}
            data={deptData}
            style={{ width: 200 }}
          />
        )}
        </>)}
      </Group>

      {/* ── มือถือ: แผ่นตัวกรองเลื่อนขึ้นจากล่าง ── */}
      <Drawer opened={filterSheetOpen} onClose={() => setFilterSheetOpen(false)}
        position="bottom" size="85%" radius="lg 0 0 0"
        title={<Text fw={700}>{t('assets.filterBtn', 'Filter')}</Text>}>
        <Stack gap="md" pb={70}>
          <Select
            label={t("common.status", "Status")}
            placeholder={t("assets.allStatuses","All statuses")}
            value={draftStatus} onChange={setDraftStatus} clearable
            data={[
              { value: 'AVAILABLE',  label: STATUS_LABEL.AVAILABLE },
              { value: 'ASSIGNED',   label: STATUS_LABEL.ASSIGNED },
              { value: 'BORROWED',   label: STATUS_LABEL.BORROWED },
              { value: 'INSTALLED',  label: STATUS_LABEL.INSTALLED },
              { value: 'REPAIR',     label: STATUS_LABEL.REPAIR },
              { value: 'DAMAGED',    label: STATUS_LABEL.DAMAGED },
              { value: 'LOST',       label: STATUS_LABEL.LOST },
              { value: 'WRITE_OFF',  label: STATUS_LABEL.WRITE_OFF },
            ]}
          />
          <Select
            label={t("assets.category", "Category")}
            placeholder={t("assets.allCategories","All categories")}
            clearable searchable
            value={draftCategory}
            onChange={(v) => {
              setDraftCategory(v)
              if (v && !subCategories.some((s: any) => s.category === v && s.code === draftSubCategory)) {
                setDraftSubCategory(null)
              }
            }}
            data={categories.map((c: any) => ({ value: c.code, label: localizedName(c, i18n.language) || c.code }))}
          />
          <Select
            label={t("assets.subCategory", "Sub-Category")}
            placeholder={t("assets.allSubCategories","All sub-categories")}
            clearable searchable
            value={draftSubCategory} onChange={setDraftSubCategory}
            data={draftSubCatData}
          />
          <Select
            label={t("assets.location", "Location")}
            placeholder={t("assets.allLocations","All locations")}
            clearable searchable
            leftSection={<IconMapPin size={14} />}
            value={draftLocation} onChange={setDraftLocation}
            data={locations.map((l: any) => ({ value: l.name, label: localizedName(l, i18n.language) }))}
          />
          {isAdminLevel && (
            <Select
              label={t("common.department", "Department")}
              placeholder={t("common.allDept","All departments")} clearable
              leftSection={<IconBuilding size={14} />}
              value={draftDept} onChange={setDraftDept}
              data={deptData}
            />
          )}
        </Stack>
        {/* ปุ่มค้างอยู่ล่างสุดของแผ่นเสมอ ไม่ต้องเลื่อนหาตอนตัวกรองยาว */}
        <Box style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e2e8f0', padding: 16 }}>
          <Group grow>
            <Button variant="default" onClick={clearFilterSheet}>{t('assets.clearFilters', 'Clear All')}</Button>
            <Button onClick={applyFilterSheet} style={{ background: 'var(--brand-primary,#0f172a)', color: '#fff' }}>
              {t('assets.applyFilters', 'Apply Filters')}
            </Button>
          </Group>
        </Box>
      </Drawer>

      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'auto' }}>
        {loading ? (isMobile ? (
          <Stack gap={0}>
            {Array.from({ length: 6 }).map((_, i) => <AssetCardSkeleton key={i} />)}
          </Stack>
        ) : (
          <Table highlightOnHover>
            <Table.Thead style={{ background: '#f8f7f4' }}>
              <Table.Tr>
                <Table.Th style={{ width: 36 }} />
                <Table.Th>{t("assets.colImage","Image")}</Table.Th>
                <Table.Th>{t("assets.colCode","Asset Code")}</Table.Th>
                <Table.Th>{t("assets.colName","Name")}</Table.Th>
                <Table.Th>{t("assets.colDept","Department")}</Table.Th>
                <Table.Th>{t("assets.colCategory","Category")}</Table.Th>
                <Table.Th>{t("assets.colBrand","Brand")}</Table.Th>
                <Table.Th>{t("assets.colModel","Model")}</Table.Th>
                <Table.Th>{t("assets.colSerial","Serial No.")}</Table.Th>
                <Table.Th>{t("assets.colLocation","Location")}</Table.Th>
                <Table.Th>{t("assets.colStatus","Status")}</Table.Th>
                <Table.Th>{t("assets.colLicense","License")}</Table.Th>
                <Table.Th>{t("assets.colWarranty","Warranty Exp.")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {Array.from({ length: 8 }).map((_, i) => <AssetRowSkeleton key={i} />)}
            </Table.Tbody>
          </Table>
        )) : isMobile ? (
          // ── Mobile Card View ─────────────────────────────────────────────
          <Stack gap={0}>
            {filtered.length === 0 ? (
              <Center p="xl"><Text c="dimmed">{t("assets.noAssetsFound", "No assets found")}</Text></Center>
            ) : filtered.map((a) => (
              <Box key={a.id} p="md" style={{
                borderBottom: '1px solid #f0f0f0',
                background: highlightCode === a.assetCode ? '#fef9c3' : selectedIds.has(a.id) ? '#f0fdf4' : undefined,
                outline: highlightCode === a.assetCode ? '2px solid #facc15' : undefined,
                transition: 'background 0.5s',
                cursor: 'pointer',
              }}
              onClick={() => { setQuickViewAsset(a); setQuickViewOpen(true) }}>
                <Group gap="sm" align="flex-start" wrap="nowrap">
                  {/* Checkbox */}
                  <Checkbox
                    checked={selectedIds.has(a.id)}
                    onChange={() => toggleSelect(a.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0, marginTop: 4 }}
                  />
                  {/* Thumbnail */}
                  <Box style={{ flexShrink: 0 }}>
                    {(() => {
                      // ── เดิมเช็คแค่รูป Model เท่านั้น ไม่เคยเช็ค a.imageUrl (รูปเฉพาะของ asset ตัว
                      // นั้นจริงๆ) เลย เหมือนกับตาราง Desktop — แก้ให้ตรงกัน: รูปเฉพาะของ asset ก่อน
                      // เสมอ ถ้าไม่มีค่อย fallback ไปรูป Model พร้อม badge เล็กๆ บอกว่าเป็นรูปเฉพาะจริง ──
                      const modelImg = allModels.find((m: any) => m.code === a.model)?.imageUrl
                      const hasOwnPhoto = !!a.imageUrl
                      const src = a.imageUrl ?? modelImg
                      return src ? (
                        <Box style={{ position: 'relative', width: 56, height: 56 }}>
                          <Image src={src} w={56} h={56} radius="md" fit="cover" style={{ border: '1px solid #ebebeb' }} />
                          {hasOwnPhoto && (
                            <Box style={{
                              position: 'absolute', bottom: -3, right: -3, width: 18, height: 18, borderRadius: '50%',
                              background: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '2px solid #fff',
                            }}>
                              <IconCamera size={10} color="#fff" />
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box w={56} h={56} style={{ background: '#f0f0f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconPhoto size={22} color="#bbb" />
                        </Box>
                      )
                    })()}
                  </Box>

                  {/* Info */}
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Box style={{ minWidth: 0 }}>
                        <Text fw={700} size="sm" style={{ fontFamily: 'Inter, sans-serif' }} truncate>{a.assetCode}</Text>
                        <Text size="sm" truncate>{a.name}</Text>
                        <Group gap={4} mt={4} wrap="wrap">
                          <Badge color={STATUS_COLOR[a.status] ?? 'gray'} variant="light" size="xs">{STATUS_LABEL[a.status] ?? a.status}</Badge>
                          {a.departmentCode && (
                            <Badge size="xs" color="teal" variant="light">
                              {departments.find(d => d.code === a.departmentCode)?.name ?? a.departmentCode}
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed" mt={2}>SN: {a.serialNumber ?? '-'}</Text>
                        {(a.brand || a.model) && (
                          <Text size="xs" c="dimmed">
                            {[a.brand, a.model].filter(Boolean).join(' · ')}
                          </Text>
                        )}
                        {a.location && <Text size="xs" c="dimmed">📍 {a.location}</Text>}
                        {a.accessories?.length > 0 && (
                          <Text size="xs" c="teal">🧩 {a.accessories.length} accessory item{a.accessories.length > 1 ? 's' : ''}</Text>
                        )}
                        {(assetLicenseMap[a.assetCode] ?? []).length > 0 && (
                          <Group gap={4} mt={4} wrap="wrap">
                            {(assetLicenseMap[a.assetCode] ?? []).map((lic: any) => (
                              <Badge key={lic.licenseId} size="xs" color="violet" variant="light">
                                🔑 {lic.productName}
                              </Badge>
                            ))}
                          </Group>
                        )}
                        {a.warrantyLifetime ? (
                          <Text size="xs" c="green">🛡 ∞ Lifetime</Text>
                        ) : a.warrantyEnd && (
                          <Text size="xs" c={new Date(a.warrantyEnd) < new Date() ? 'red' : 'dimmed'}>
                            🛡 {a.warrantyEnd.slice(0, 10)}
                          </Text>
                        )}
                        {/* Tap hint — subtle เพราะ card ทั้งใบ clickable แล้ว */}
                        <Text size="xs" c="teal" mt={4} style={{ opacity: 0.6 }}>{t('assets.tapToManage')}</Text>
                      </Box>
                    </Group>
                  </Box>
                </Group>
              </Box>
            ))}
          </Stack>
        ) : (
          // ── Desktop/Tablet Table View ─────────────────────────────────────
          <ScrollArea>
          <Table highlightOnHover>
            <Table.Thead style={{ background: '#f8f7f4' }}>
              <Table.Tr>
                <Table.Th style={{ width: 36 }}>
                  <Checkbox
                    size="xs"
                    checked={assets.length > 0 && selectedIds.size === assets.length}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < assets.length}
                    onChange={toggleSelectAll}
                  />
                </Table.Th>
                <Table.Th>{t("assets.colImage","Image")}</Table.Th>
                <SortableTh column="assetCode">{t("assets.colCode","Asset Code")}</SortableTh>
                <SortableTh column="name">{t("assets.colName","Name")}</SortableTh>
                <Table.Th>{t("assets.colDept","Department")}</Table.Th>
                <Table.Th>{t("assets.colCategory","Category")}</Table.Th>
                <SortableTh column="brand">{t("assets.colBrand","Brand")}</SortableTh>
                <SortableTh column="model">{t("assets.colModel","Model")}</SortableTh>
                <Table.Th>{t("assets.colSerial","Serial No.")}</Table.Th>
                <Table.Th>{t("assets.colLocation","Location")}</Table.Th>
                <Table.Th>{t("assets.colStatus","Status")}</Table.Th>
                <Table.Th>{t("assets.colLicense","License")}</Table.Th>
                <Table.Th>{t("assets.colWarranty","Warranty Exp.")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={12}>
                    <Center p="xl"><Text c="dimmed">{t("assets.noAssetsFound", "No assets found")}</Text></Center>
                  </Table.Td>
                </Table.Tr>
              ) : filtered.map((a) => (
                <Table.Tr key={a.id}
                  style={{
                    background: highlightCode === a.assetCode
                      ? '#fef9c3'
                      : selectedIds.has(a.id) ? '#f0fdf4' : undefined,
                    transition: 'background 0.5s',
                    outline: highlightCode === a.assetCode ? '2px solid #facc15' : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    // ไม่เปิด QuickView ถ้ากด checkbox หรือ action button
                    const target = e.target as HTMLElement
                    if (target.closest('input[type="checkbox"]') || target.closest('[data-action]')) return
                    setQuickViewAsset(a); setQuickViewOpen(true)
                  }}
                >
                  <Table.Td onClick={(e) => { e.stopPropagation(); toggleSelect(a.id) }} data-action="true"
                    style={{ cursor: 'pointer', width: 36 }}>
                    {selectedIds.has(a.id)
                      ? <Box style={{ width: 20, height: 20, borderRadius: '50%', background: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Text size="xs" c="white" fw={700}>✓</Text>
                        </Box>
                      : <Box style={{ width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #cbd5e1' }} />
                    }
                  </Table.Td>
                  <Table.Td>
                    {(() => {
                      // ── เดิมโชว์แค่รูป Model (รูปทั่วไป) เท่านั้น ไม่เคยเช็ค a.imageUrl (รูปเฉพาะ
                      // ของ asset ตัวนั้นจริงๆ ที่อัปโหลดตอนแก้ไข) เลยสักจุด — แก้ให้เรียงลำดับความ
                      // สำคัญถูกต้อง: รูปเฉพาะของ asset ก่อนเสมอ ถ้าไม่มีค่อย fallback ไปรูป Model
                      // (ตรงกับ logic ที่ฟอร์ม Edit ใช้อยู่แล้ว: editing?.imageUrl ?? modelImage)
                      // พร้อม badge เล็กๆ มุมล่างขวา บอกว่า "รูปนี้เป็นรูปเฉพาะจริง" ไม่ใช่รูปทั่วไป
                      // ของ Model ที่ asset ทุกตัวรุ่นเดียวกันจะเห็นเหมือนกันหมด ──
                      const modelImg = allModels.find((m: any) => m.code === a.model)?.imageUrl
                      const hasOwnPhoto = !!a.imageUrl
                      const src = a.imageUrl ?? modelImg
                      return src ? (
                        <Box style={{ position: 'relative', width: 40, height: 40 }}>
                          <Image src={src} w={40} h={40} radius="sm" fit="cover" style={{ border: '1px solid #ebebeb' }} />
                          {hasOwnPhoto && (
                            <Tooltip label={t("assets.hasOwnPhotoTooltip", "This asset has its own photo")} withArrow>
                              <Box style={{
                                position: 'absolute', bottom: -3, right: -3, width: 15, height: 15, borderRadius: '50%',
                                background: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1.5px solid #fff',
                              }}>
                                <IconCamera size={9} color="#fff" />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Box w={40} h={40} style={{ background: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconPhoto size={18} color="#bbb" />
                        </Box>
                      )
                    })()}
                  </Table.Td>
                  <Table.Td><Text fw={700} size="sm" style={{ fontFamily: 'Inter, sans-serif' }}>{a.assetCode}</Text></Table.Td>
                  <Table.Td><Text size="sm">{a.name}</Text></Table.Td>
                  <Table.Td>
                    {a.departmentCode ? (
                      <Badge size="xs" color="teal" variant="light" leftSection={<IconBuilding size={10} />}>
                        {departments.find(d => d.code === a.departmentCode)?.name ?? a.departmentCode}
                      </Badge>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{a.category}{a.subCategory ? ` / ${a.subCategory}` : ''}</Text></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{a.brand ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{a.model ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{a.serialNumber}</Text></Table.Td>
                  <Table.Td><Text size="sm">{a.location ?? '-'}</Text></Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[a.status] ?? 'gray'} variant="light">{STATUS_LABEL[a.status] ?? a.status}</Badge>
                  </Table.Td>
                  {/* License badges */}
                  <Table.Td>
                    {(assetLicenseMap[a.assetCode] ?? []).length > 0 ? (
                      <Stack gap={2}>
                        {(assetLicenseMap[a.assetCode] ?? []).map((lic: any) => (
                          <Tooltip key={lic.licenseId} label={`${lic.productName} — expires: ${lic.expiryDate?.slice(0,10) ?? '∞'}`}>
                            <Badge size="xs" color="violet" variant="light" style={{ cursor: 'default', maxWidth: 100 }}>
                              🔑 {lic.productName}
                            </Badge>
                          </Tooltip>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {a.warrantyLifetime ? (
                      <Badge color="green" variant="light" size="xs">∞ Lifetime</Badge>
                    ) : (
                      <Text size="xs" c={a.warrantyEnd && new Date(a.warrantyEnd) < new Date() ? 'red' : 'dimmed'}>
                        {a.warrantyEnd ? a.warrantyEnd.slice(0, 10) : '-'}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </ScrollArea>
        )}
      </Box>

      {/* ── Pagination Controls ── */}
      <Group justify="space-between" mt="md" wrap="wrap" gap="sm">
        <Group gap="xs" align="center">
          <Text size="sm" c="dimmed">{t("assets.show", "Show")}</Text>
          <Select
            size="xs"
            value={String(pageSize)}
            onChange={(v) => { setPageSize(Number(v)); setPage(1) }}
            data={[
              { value: '50',   label: '50 per page' },
              { value: '100',  label: '100 per page' },
              { value: '200',  label: '200 per page' },
              { value: '400',  label: '400 per page' },
              { value: '1000', label: '1,000 per page' },
            ]}
            style={{ width: 130 }}
          />
          <Text size="sm" c="dimmed">
            of <b>{totalRecords.toLocaleString()}</b> total
          </Text>
        </Group>
        {totalPages > 1 && (
          <Pagination
            value={page}
            onChange={setPage}
            total={totalPages}
            size="sm"
            color="teal"
            styles={{ control: { fontFamily: 'Inter, sans-serif' } }}
          />
        )}
      </Group>

      {/* ── Add / Edit Modal ── */}
      <Modal opened={opened} onClose={() => { close(); setIsCloning(false) }}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>
          {isCloning ? t('assets.cloneAsset','🗐 Clone Asset') : editing ? t('assets.editAsset','Edit Asset') : t('assets.addNew','Add New Asset')}
        </Text>}
        size="xl" radius="lg" scrollAreaComponent={ScrollArea.Autosize}>
        <Stack gap="sm">

          {/* ── Model Image (read-only, top) ── */}
          {(() => {
            const selectedModel = allModels.find((m: any) => m.code === form.model)
            return selectedModel?.imageUrl ? (
              <Box>
                <Text size="sm" fw={600} mb={6} c="dimmed">{t("assets.modelImage", "Model Image")}</Text>
                <Group gap="md" align="center">
                  <Image src={selectedModel.imageUrl} w={100} h={100} fit="cover" radius="md" style={{ border: '1px solid #ebebeb' }} />
                  <Box>
                    <Text size="sm" fw={600}>{selectedModel.name}</Text>
                    <Text size="xs" c="dimmed">{selectedModel.code}</Text>
                  </Box>
                </Group>
              </Box>
            ) : null
          })()}

          <Divider />

          {isCloning && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm" fw={600}>Cloning asset — please fill in new Name and Serial Number</Text>
              <Text size="xs" c="dimmed">Other fields are pre-filled from the original — edit as needed</Text>
            </Alert>
          )}

          {/* ────────────────────────────────────────────────────────────
              SECTION 1 — IDENTITY (สีฟ้า): Brand, Model, Category, Name
          ──────────────────────────────────────────────────────────── */}
          <Box style={{ background: '#eff6ff', borderRadius: 10, padding: '14px 16px', border: '1px solid #bfdbfe' }}>
            <Text size="xs" fw={700} c="blue" mb={10} tt="uppercase" style={{ letterSpacing: '0.6px' }}>
              🏷 Asset Identity
            </Text>
          <Grid gutter="sm">

            {/* ── Row 1: Brand + Model ── */}
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select
                    label={t("assets.labelBrand","Brand")}
                    placeholder={t("assets.selectBrand", "Select brand")}
                    data={brands.map((b) => ({ value: b.code, label: localizedNameWithEn(b, i18n.language) }))}
                    value={form.brand || null}
                    onChange={handleBrandChange}
                    searchable clearable
                  />
                </Box>
                <Tooltip label={t('assets.addBrand')}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1} onClick={() => openQuickAdd('brand')}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select
                    label={t("assets.colModel","Model")}
                    placeholder={form.brand ? 'Select model' : 'Select brand first'}
                    data={models.map((m) => ({ value: m.code, label: m.name }))}
                    value={form.model || null}
                    onChange={(v) => {
                      f('model')(v ?? '')
                      // Auto-fill Category & Sub-Category จาก Model
                      if (v) {
                        const m = allModels.find((x: any) => x.code === v)
                        if (m?.categoryCode) {
                          setForm(p => ({
                            ...p,
                            model: v,
                            category: m.categoryCode ?? p.category,
                            subCategory: m.subCategoryCode ?? '',
                          }))
                          // Re-generate asset code ถ้ามี dept แล้ว
                          if (!editing && form.departmentCode && m.categoryCode) {
                            fetchNextCode(form.departmentCode, m.categoryCode)
                          }
                        }
                      }
                    }}
                    disabled={!form.brand}
                    searchable clearable
                  />
                </Box>
                <Tooltip label={form.brand ? t('assets.addModel','+ Add Model') : t('assets.selectBrandFirst','Select a brand first')}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1} disabled={!form.brand}
                    onClick={() => openQuickAdd('model', form.brand)}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>

            {/* ── Row 2: Category + Sub-Category (auto-filled จาก Model) ── */}
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select
                    label={<Text size="sm" fw={500}>Category {<Text span c="red">*</Text>}</Text>}
                    placeholder={t("assets.selectCategory", "Select category")}
                    data={catData}
                    value={form.category || null}
                    onChange={handleCategoryChange}
                    searchable clearable
                    error={triedSave && !form.category ? 'กรุณาเลือก Category' : undefined}
                  />
                </Box>
                <Tooltip label={t('assets.addCategory')}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={triedSave && !form.category ? 22 : 1} onClick={() => openQuickAdd('category')}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select label={t("assets.subCategory","Sub Category")}
                    placeholder={form.category ? 'Select sub category' : 'Select category first'}
                    data={subCatData} value={form.subCategory || null}
                    onChange={(v) => f('subCategory')(v ?? '')}
                    disabled={!form.category} searchable clearable />
                </Box>
                <Tooltip label={t('assets.addSubCategory')}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1}
                    disabled={!form.category}
                    onClick={() => openQuickAdd('subcategory', form.category)}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>

          </Grid>
          </Box>

          {/* ────────────────────────────────────────────────────────────
              SECTION 2 — LOCATION & CODE (สีเขียว): Dept, Code, Name, SN, PO
          ──────────────────────────────────────────────────────────── */}
          <Box style={{ background: '#f0fdf4', borderRadius: 10, padding: '14px 16px', border: '1px solid #bbf7d0' }}>
            <Text size="xs" fw={700} c="teal" mb={10} tt="uppercase" style={{ letterSpacing: '0.6px' }}>
              📍 Department & Identification
            </Text>
          <Grid gutter="sm">

            {/* ── Row 3: Department ── */}
            <Grid.Col span={12}>
              <Select
                label={
                  <Group gap={6}>
                    <IconBuilding size={14} />
                    <Text size="sm" fw={500}>{t("common.department", "Department")}</Text>
                  </Group>
                }
                description={isAdminLevel ? undefined : `You can only assign assets to your departments: ${userDepts.join(', ')}`}
                placeholder={t("assets.selectDepartment", "Select department")}
                data={isAdminLevel ? deptData : deptData.filter((d) => userDepts.includes(d.value))}
                value={form.departmentCode || null}
                onChange={(v) => {
                  f('departmentCode')(v ?? '')
                  if (!editing && v && form.category) {
                    fetchNextCode(v, form.category)
                  }
                }}
                searchable clearable
              />
            </Grid.Col>

            {/* ── Row 4: Asset Code + Asset Name ── */}
            <Grid.Col span={6}>
              <TextInput
                label={t("assets.labelAssetCode","Asset Code *")}
                placeholder={editing ? '' : (form.departmentCode && form.category ? 'กำลังสร้าง...' : 'เลือก Dept + Category ก่อน')}
                value={form.assetCode}
                readOnly={true}
                onChange={undefined}
                error={codeCheck === 'dup' ? 'Asset Code นี้มีอยู่แล้ว' : undefined}
                styles={{
                  input: {
                    background: '#f8fafc',
                    fontWeight: 600,
                    fontFamily: 'Inter, sans-serif',
                    letterSpacing: '0.3px',
                    ...(codeCheck === 'dup' ? { borderColor: '#ef4444' } : codeCheck === 'ok' ? { borderColor: '#22c55e' } : {}),
                  }
                }}
                rightSectionWidth={52}
                rightSection={
                  <Group gap={2} wrap="nowrap">
                    {codeLoading ? <Loader size={14} color="gray" /> :
                     codeCheck === 'ok'  ? <IconCheck size={16} color="#22c55e" /> :
                     codeCheck === 'dup' ? <IconAlertCircle size={16} color="#ef4444" /> :
                     null}
                    {!editing && (
                      <Tooltip label={t('assets.generateCode')}>
                        <ActionIcon variant="subtle" color="gray" size="sm"
                          loading={codeLoading}
                          disabled={!form.departmentCode || !form.category}
                          onClick={() => fetchNextCode(form.departmentCode, form.category)}>
                          <IconRefresh size={13} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                }
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label={t("assets.labelAssetName","Asset Name *")}
                placeholder="MacBook Pro 14"
                value={form.name}
                onChange={(e) => f('name')(e.target.value)}
                styles={isCloning && !form.name ? { input: { borderColor: '#f59e0b', borderWidth: 2 } } : undefined}
              />
            </Grid.Col>

            {/* ── Serial Number + PO Number ── */}
            <Grid.Col span={6}>
              <TextInput
                label={t("assets.labelSerial","Serial Number *")}
                placeholder="SN-001"
                value={form.serialNumber}
                onChange={(e) => f('serialNumber')(e.target.value)}
                error={snCheck === 'dup' ? t('assets.dupSerial','Serial Number นี้มีอยู่แล้ว') : undefined}
                styles={
                  snCheck === 'dup' ? { input: { borderColor: '#ef4444' } } :
                  snCheck === 'ok'  ? { input: { borderColor: '#22c55e' } } :
                  isCloning && !form.serialNumber ? { input: { borderColor: '#f59e0b', borderWidth: 2 } } :
                  undefined
                }
                rightSectionWidth={64}
                rightSection={
                  <Group gap={2} wrap="nowrap">
                    {snCheck === 'checking' && <Loader size={13} color="gray" />}
                    {snCheck === 'ok'       && <IconCheck size={15} color="#22c55e" />}
                    {snCheck === 'dup'      && <IconAlertCircle size={15} color="#ef4444" />}
                    <Tooltip label={t('assets.scanBarcode')}>
                      <ActionIcon variant="subtle" color="gray" onClick={openScanner}>
                        <IconScan size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                }
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t("assets.labelPONumber","PO Number")} value={form.poNumber} onChange={(e) => f('poNumber')(e.target.value)} />
            </Grid.Col>

          </Grid>
          </Box>

          {/* ────────────────────────────────────────────────────────────
              SECTION 3 — PURCHASE & WARRANTY (สีม่วง): Vendor, Price, Warranty, Location
          ──────────────────────────────────────────────────────────── */}
          <Box style={{ background: '#faf5ff', borderRadius: 10, padding: '14px 16px', border: '1px solid #e9d5ff' }}>
            <Text size="xs" fw={700} c="violet" mb={10} tt="uppercase" style={{ letterSpacing: '0.6px' }}>
              💰 Purchase & Warranty
            </Text>
          <Grid gutter="sm">

            {/* ── Vendor + Purchase Date ── */}
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select
                    label={t("assets.labelVendor","Vendor")}
                    placeholder={t("assets.selectOrTypeVendor", "Select or type vendor")}
                    data={vendors.map((v: any) => ({ value: v.name, label: localizedName(v, i18n.language) }))}
                    value={form.vendor || null}
                    onChange={(v) => f('vendor')(v ?? '')}
                    searchable
                    clearable
                    filter={({ options, search }) =>
                      (options as any[]).filter((o) =>
                        o.label.toLowerCase().includes(search.toLowerCase())
                      )
                    }
                    nothingFoundMessage={
                      <Text size="xs" c="dimmed">ไม่พบ vendor — กด + เพื่อเพิ่มใหม่</Text>
                    }
                  />
                </Box>
                <Tooltip label={t('assets.addVendor')}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1} onClick={() => openQuickAdd('vendor')}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <SmartDateInput
                label={t("assets.labelPurchaseDate","Purchase Date")}
                placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                value={form.purchaseDate}
                onChange={(v) => f('purchaseDate')(v)}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t("assets.purchasePriceExclVat","Purchase Price (Excl. VAT)")} type="number" placeholder="0.00" value={form.purchasePrice} onChange={(e) => f('purchasePrice')(e.target.value)} />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t("assets.purchasePriceInclVat","Purchase Price (Incl. VAT)")} type="number" placeholder="0.00" value={form.purchasePriceVat} onChange={(e) => f('purchasePriceVat')(e.target.value)} />
            </Grid.Col>
            <Grid.Col span={6}>
              <SmartDateInput
                label={t("assets.labelWarrantyStart","Warranty Start")}
                placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                value={form.warrantyStart}
                onChange={(v) => f('warrantyStart')(v)}
                clearable
              />
              <Checkbox
                label={t("assets.labelWarrantyLifetime","Lifetime Warranty")}
                checked={form.warrantyLifetime as boolean}
                onChange={(e) => setForm(p => ({ ...p, warrantyLifetime: e.currentTarget.checked, warrantyEnd: '' }))}
                mt={10}
                color="teal"
                styles={{ label: { fontWeight: 600, fontSize: 14 } }}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Box>
                {!form.warrantyLifetime && (
                  <SmartDateInput
                    label={t("assets.labelWarrantyEnd","Warranty End")}
                    placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                    value={form.warrantyEnd}
                    onChange={(v) => f('warrantyEnd')(v)}
                    clearable
                  />
                )}
                {form.warrantyLifetime && (
                  <Box mt={24} style={{ background: '#f0fdf9', border: '1px solid #0d9488', borderRadius: 8, padding: '8px 12px' }}>
                    <Text size="sm" fw={600} c="green">∞ ประกันตลอดชีพ (Lifetime)</Text>
                  </Box>
                )}
              </Box>
            </Grid.Col>
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select
                    label={t("assets.location", "Location")}
                    placeholder={t("assets.selectLocation", "Select location")}
                    data={[
                      { value: '', label: '— None —' },
                      ...locations.map((l) => ({ value: l.name, label: localizedName(l, i18n.language) }))
                    ]}
                    value={form.location || null}
                    onChange={(v) => f('location')(v ?? '')}
                    searchable clearable
                  />
                </Box>
                <Tooltip label={t('assets.addLocation')}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1} onClick={() => openQuickAdd('location')}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>

          </Grid>
          </Box>

          {/* ────────────────────────────────────────────────────────────
              SECTION 4 — PHOTO & NOTES (สีเทา)
          ──────────────────────────────────────────────────────────── */}
          <Box style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
            <Text size="xs" fw={700} c="gray" mb={10} tt="uppercase" style={{ letterSpacing: '0.6px' }}>
              📎 Photo & Notes
            </Text>

          {/* ── Current Asset Photo (bottom) ── */}
          <Divider label={t("assets.currentPhoto","Current Asset Photo")} labelPosition="left" />
          <Box>
            <Text size="xs" c="dimmed" mb={8}>รูปถ่ายปัจจุบันของ Asset นี้ (แยกจากรูป Model)</Text>
            <Group align="flex-start" gap="md">
              <Box w={100} h={100} style={{ background: '#f8f7f4', borderRadius: 10, border: '1.5px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {imagePreview ? <Image src={imagePreview} w={100} h={100} fit="cover" /> : <IconPhoto size={28} color="#ccc" />}
              </Box>
              <Stack gap="xs" justify="center">
                <FileButton onChange={handleImagePick} accept="image/*">
                  {(props) => (
                    <Button {...props} size="sm" variant="outline" leftSection={<IconUpload size={14} />} color="gray">
                      {imageFile ? imageFile.name : imagePreview ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                  )}
                </FileButton>
                {imagePreview && (
                  <Button size="xs" variant="subtle" color="red" onClick={() => { setImageFile(null); setImagePreview(null) }}>{t('handover.remove')}</Button>
                )}
                <Text size="xs" c="dimmed">JPG, PNG, WEBP — max 5 MB</Text>
              </Stack>
            </Group>
          </Box>

          <Box>
          </Box>

          {/* Notes / Internal Remarks */}
          <Textarea
            label={t("assets.notes","Notes / Internal Remarks")}
            placeholder={t("assets.internalNotesPlaceholder","Internal notes about this asset (not shown on reports)...")}
            value={(form as any).notes ?? ''}
            onChange={(e) => setForm((p: any) => ({ ...p, notes: e.target.value }))}
            minRows={2} autosize maxRows={4}
          />

          </Box> {/* end Section 4 */}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={close}>{t("common.cancel", "Cancel")}</Button>
            <Button
              loading={saving}
              disabled={codeCheck === 'dup' || snCheck === 'dup'}
              onClick={handleSave}
              style={{ background: codeCheck === 'dup' || snCheck === 'dup' ? '#d1d5db' : '#0f172a', color: '#ffffff', fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>
              {isCloning ? t('assets.createClone','Create Clone') : editing ? t('assets.saveChanges','Save Changes') : t('assets.addAsset','Add Asset')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ImportModal opened={importOpened} onClose={closeImport} onDone={loadAssets} onDownloadTemplate={downloadImportTemplate} />

      {/* ── Transfer Department Modal ── */}
      <Modal opened={transferOpened} onClose={closeTransfer} title={
        <Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('assets.transferDepartment', 'Transfer Department')}</Text>
      } size="sm" radius="lg">
        <Stack gap="md">
          <Box style={{ background: '#f0fdf4', borderRadius: 10, padding: 12, border: '1px solid #bbf7d0' }}>
            <Text size="sm" fw={700} c="teal">{selectedIds.size} asset(s) selected</Text>
            <Text size="xs" c="dimmed">จะย้าย department ของ asset ทั้งหมดที่เลือกพร้อมกัน</Text>
          </Box>
          <Select
            label={t("assets.labelDept","Transfer to Department *")}
            placeholder={t("assets.selectTargetDepartment", "Select target department")}
            data={departments.map((d) => ({ value: d.code, label: `${d.name} (${d.code})` }))}
            value={transferDept}
            onChange={setTransferDept}
            searchable
          />
          <Textarea label={t("common.note", "Note")} placeholder={t("assets.reasonForTransfer", "Reason for transfer...")} minRows={2} value={transferNote} onChange={(e) => setTransferNote(e.target.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeTransfer}>{t("common.cancel", "Cancel")}</Button>
            <Button loading={transferring} onClick={handleTransfer}
              leftSection={<IconArrowsTransferUp size={16} />}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              Transfer {selectedIds.size} Asset(s)
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Move Location Modal ── */}
      <Modal opened={locationOpened} onClose={closeLocation} title={
        <Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('assets.moveLocation', 'Move Location')}</Text>
      } size="sm" radius="lg">
        <Stack gap="md">
          {locationAsset && (
            <Box style={{ background: '#f8f7f4', borderRadius: 10, padding: 12 }}>
              <Text size="sm" fw={700}>{locationAsset.name}</Text>
              <Text size="xs" c="dimmed">{locationAsset.assetCode} · Current location: <b>{locationAsset.location ?? '-'}</b></Text>
            </Box>
          )}
          <Select
            label={t("assets.newLocation","New Location")+' *'}
            placeholder={t("assets.selectLocation", "Select location")}
            data={locations.map((l) => ({ value: l.name, label: `${localizedName(l, i18n.language)} (${l.code})` }))}
            value={newLocation}
            onChange={setNewLocation}
            searchable
          />
          <Textarea label={t("common.note", "Note")} placeholder={t("assets.reasonForMove", "Reason for move...")} minRows={2} value={locationNote} onChange={(e) => setLocationNote(e.target.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeLocation}>{t("common.cancel", "Cancel")}</Button>
            <Button loading={movingLocation} onClick={handleMoveLocation}
              leftSection={<IconMapPin size={16} />}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t("common.move","Move")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Asset History Modal ── */}
      <Modal opened={historyOpened} onClose={closeHistory} title={
        <Group gap="xs">
          <IconHistory size={20} />
          <Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>
            Asset History {historyAsset ? `— ${historyAsset.name}` : ''}
          </Text>
        </Group>
      } size="lg" radius="lg">
        <ScrollArea h={480}>
          {loadingHistory ? (
            <Center p="xl"><Loader color="teal" /></Center>
          ) : historyData.length === 0 ? (
            <Center p="xl"><Text c="dimmed">{t('common.noHistory', 'ไม่มีประวัติ')}</Text></Center>
          ) : (
            <Timeline active={historyData.length} bulletSize={28} lineWidth={2} mt="sm">
              {historyData.map((h: any) => {
                const typeConfig: Record<string, { color: string; label: string; icon: string }> = {
                  LOCATION_MOVE:   { color: 'teal',   label: 'Location Move',      icon: '📍' },
                  LOCATION_CHANGE: { color: 'teal',   label: 'Location Change',    icon: '📍' },
                  DEPT_TRANSFER:   { color: 'blue',   label: 'Dept Transfer',      icon: '🔀' },
                  OWNER_CHANGE:    { color: 'violet', label: 'Owner Change',       icon: '👤' },
                  STATUS_CHANGE:   { color: 'orange', label: 'Status Change',      icon: '🔄' },
                  BORROW:          { color: 'yellow', label: 'Borrowed',           icon: '📤' },
                  RETURN:          { color: 'lime',   label: 'Returned',           icon: '📥' },
                  ASSET_EDIT:      { color: 'blue',   label: 'Edited',             icon: '✏️' },
                }
                const cfg = typeConfig[h.type] ?? { color: 'gray', label: h.type, icon: '•' }
                // ASSET_EDIT note มีหลาย field คั่นด้วย " | "
                const editLines = h.type === 'ASSET_EDIT' && h.note
                  ? h.note.split(' | ')
                  : null
                return (
                  <Timeline.Item
                    key={h.id}
                    bullet={<Text size="xs">{cfg.icon}</Text>}
                    title={
                      <Group gap={6}>
                        <Badge color={cfg.color} variant="light" size="sm">{cfg.label}</Badge>
                        <Text size="xs" c="dimmed">
                          {new Date(h.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </Text>
                      </Group>
                    }
                  >
                    <Box mt={4}>
                      {/* ASSET_EDIT: แสดงทีละ field */}
                      {editLines ? (
                        <Stack gap={4} mb={4}>
                          {editLines.map((line: string, i: number) => {
                            const [fieldPart, rest] = line.split(': "')
                            const parts = rest?.split('" → "')
                            const from = parts?.[0]
                            const to = parts?.[1]?.replace(/"$/, '')
                            return (
                              <Group key={i} gap={4} wrap="nowrap" align="center">
                                <Text size="xs" fw={500} c="dimmed" style={{ minWidth: 100 }}>{fieldPart}</Text>
                                <Text size="xs" style={{ background: '#fee2e2', padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>{from ?? '-'}</Text>
                                <Text size="xs" c="dimmed">→</Text>
                                <Text size="xs" style={{ background: '#dcfce7', padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>{to ?? '-'}</Text>
                              </Group>
                            )
                          })}
                        </Stack>
                      ) : h.fromValue && h.toValue ? (
                        <Group gap={6} mb={2}>
                          <Text size="xs" style={{ background: '#fee2e2', padding: '2px 8px', borderRadius: 4 }}>{h.fromValue}</Text>
                          <Text size="xs" c="dimmed">→</Text>
                          <Text size="xs" style={{ background: '#dcfce7', padding: '2px 8px', borderRadius: 4 }}>{h.toValue}</Text>
                        </Group>
                      ) : null}
                      {h.performedBy && <Text size="xs" c="dimmed">By: <b>{h.performedBy}</b></Text>}
                      {h.note && !editLines && <Text size="xs" c="dimmed" mt={2}>{h.note}</Text>}
                    </Box>
                  </Timeline.Item>
                )
              })}
            </Timeline>
          )}

          {/* ── Incident Reports section ── */}
          {(historyAsset?.lostIncidentUrl || historyAsset?.damagedIncidentUrl) && (
            <Box mt="lg" pt="md" style={{ borderTop: '1px solid #f1f5f9' }}>
              <Text size="xs" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                {t("assets.incidentReports","Incident Reports")}
              </Text>
              <Stack gap={6}>
                {historyAsset?.lostIncidentUrl && (
                  <Box p={10} style={{ background: '#fdf4ff', borderRadius: 8, border: '1px solid #e9d5ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <Group gap={8} wrap="nowrap">
                      <IconFile size={16} color="#9c36b5" style={{ flexShrink: 0 }} />
                      <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} c="grape.7">Lost — Incident Report</Text>
                        <Text size="xs" c="dimmed" truncate>{t('lostReport.pdfAttached', 'แนบ PDF แล้ว')}</Text>
                      </Box>
                    </Group>
                    <Button
                      component="a"
                      href={historyAsset.lostIncidentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      variant="light"
                      color="grape"
                      leftSection={<IconExternalLink size={13} />}
                      style={{ flexShrink: 0 }}
                    >
                      {t("common.openPdf","Open PDF")}
                    </Button>
                  </Box>
                )}
                {historyAsset?.damagedIncidentUrl && (
                  <Box p={10} style={{ background: '#fff1f2', borderRadius: 8, border: '1px solid #fecdd3', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <Group gap={8} wrap="nowrap">
                      <IconFile size={16} color="#ef4444" style={{ flexShrink: 0 }} />
                      <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} c="red.7">Damaged — Incident Report</Text>
                        <Text size="xs" c="dimmed" truncate>{t('lostReport.pdfAttached', 'แนบ PDF แล้ว')}</Text>
                      </Box>
                    </Group>
                    <Button
                      component="a"
                      href={historyAsset.damagedIncidentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      variant="light"
                      color="red"
                      leftSection={<IconExternalLink size={13} />}
                      style={{ flexShrink: 0 }}
                    >
                      {t("common.openPdf","Open PDF")}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </ScrollArea>
      </Modal>

      {/* ── QuickAdd Modal ───────────────────────────────────────────────── */}
      <Modal opened={qaOpened} onClose={closeQA} size="sm" radius="lg"
        title={
          <Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 15 }}>
            {qaType === 'brand' && '+ เพิ่ม Brand ใหม่'}
            {qaType === 'category' && '+ เพิ่ม Category ใหม่'}
            {qaType === 'subcategory' && '+ เพิ่ม Sub-Category ใหม่'}
            {qaType === 'model' && '+ เพิ่ม Model ใหม่'}
            {qaType === 'vendor' && '+ เพิ่ม Vendor ใหม่'}
            {qaType === 'location' && '+ เพิ่ม Location ใหม่'}
          </Text>
        }>
        <Stack gap="sm">
          {/* Code field — ไม่แสดงสำหรับ Vendor */}
          {qaType !== 'vendor' && (
            <TextInput
              label="Code *"
              placeholder={
                qaType === 'brand' ? 'e.g. DELL, APPLE' :
                qaType === 'category' ? 'e.g. LAPTOP, MONITOR' :
                qaType === 'subcategory' ? 'e.g. GAMING-LAPTOP' :
                qaType === 'model' ? 'e.g. LATITUDE5420' :
                'e.g. IT-OFFICE-1F'
              }
              value={qaForm.code}
              onChange={e => setQAForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') }))}
              description={t('assets.assetCodeHint')}
            />
          )}
          <TextInput
            label="Name *"
            placeholder={
              qaType === 'brand' ? 'e.g. Dell, Apple' :
              qaType === 'category' ? 'e.g. Laptop, Monitor' :
              qaType === 'subcategory' ? 'e.g. Gaming Laptop' :
              qaType === 'model' ? 'e.g. Latitude 5420' :
              qaType === 'vendor' ? 'e.g. Office Depot Thailand' :
              'e.g. IT Office Floor 1'
            }
            value={qaForm.name}
            onChange={e => setQAForm(p => ({ ...p, name: e.target.value }))}
          />
          {/* Sub-Category: แสดง Parent Category (read-only หรือ select) */}
          {qaType === 'subcategory' && (
            <Select
              label="Parent Category *"
              data={catData}
              value={qaForm.parentCategory || null}
              onChange={v => setQAForm(p => ({ ...p, parentCategory: v ?? '' }))}
              searchable
              description={t('assets.subCategoryHint')}
            />
          )}
          {/* Model: แสดง Parent Brand (read-only หรือ select) */}
          {qaType === 'model' && (
            <Select
              label="Parent Brand *"
              data={brands.map((b) => ({ value: b.code, label: localizedNameWithEn(b, i18n.language) }))}
              value={qaForm.parentCategory || null}
              onChange={v => setQAForm(p => ({ ...p, parentCategory: v ?? '' }))}
              searchable
            />
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeQA}>ยกเลิก</Button>
            <Button loading={qaSaving} onClick={handleQuickAdd}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              บันทึก
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Barcode Scanner ─────────────────────────────────────────────── */}
      <BarcodeScanner
        opened={scannerOpened}
        onClose={closeScanner}
        onScan={(value) => {
          f('serialNumber')(value)
          closeScanner()
        }}
      />

      {/* ── QR Label Modal ───────────────────────────────────────────────── */}
      <AssetQRLabel
        opened={qrOpened}
        onClose={closeQR}
        assets={qrAssets.map(a => ({
          assetCode: a.assetCode,
          name: a.name,
          department: departments.find(d => d.code === a.departmentCode)?.name ?? a.departmentCode,
          serialNumber: a.serialNumber,
          location: a.location,
        }))}
      />

      {/* ── Mark as Installed Modal ────────────────────────────────────────── */}
      <Modal opened={installOpened} onClose={closeInstall} size="sm" radius="lg"
        title={<Group gap={8}><IconPlugConnected size={18} color="#0891b2" /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{t('assets.markInstalled', 'Mark as Installed')}</Text></Group>}>
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            {t('assets.installHint', 'For infrastructure assets tied to a place, not a person (AP, CCTV, Server, etc).')}
          </Text>
          <Select
            label={t('assets.installLocation', 'Installed Location') + ' *'}
            placeholder={t("assets.selectLocation", "Select location")}
            data={locations.map((l) => ({ value: l.name, label: `${localizedName(l, i18n.language)} (${l.code})` }))}
            value={installForm.location || null}
            onChange={v => setInstallForm(p => ({ ...p, location: v ?? '' }))}
            searchable
          />
          <SmartDateInput
            label={t('assets.installDate', 'Installed Date (optional)')}
            placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
            value={installForm.installedDate}
            onChange={v => setInstallForm(p => ({ ...p, installedDate: v }))}
          />
          <TextInput
            label={t('repair.noteOptional', 'Note (optional)')}
            value={installForm.note}
            onChange={e => setInstallForm(p => ({ ...p, note: e.target.value }))}
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeInstall}>{t('common.cancel', 'Cancel')}</Button>
            <Button loading={installSaving} disabled={!installForm.location.trim()} onClick={handleSubmitInstall}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t('assets.markInstalled', 'Mark as Installed')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Report Lost Modal ─────────────────────────────────────────────── */}
      <Modal opened={lostOpened} onClose={closeLost} size={isSmall ? '100%' : 'sm'} fullScreen={isSmall} radius={isMobile ? 0 : 'lg'}
        title={<Group gap={8}><IconBan size={18} color="#9c36b5" /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{t('assets.reportLostAsset', 'Report Lost Asset')}</Text></Group>}>
        <Stack gap="sm">
          {lostAsset && (
            <Box p={10} style={{ background: '#fdf4ff', borderRadius: 8, border: '1px solid #e9d5ff' }}>
              <Text size="sm" fw={600}>{lostAsset.name}</Text>
              <Text size="xs" c="dimmed">{lostAsset.assetCode} · {lostAsset.serialNumber}</Text>
            </Box>
          )}
          <SmartDateInput label={t("assets.dateLost", "Date Lost")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={lostForm.lostDate}
            onChange={v => setLostForm(p => ({ ...p, lostDate: v }))} clearable />
          <TextInput label={t('lostReport.lastLocation')} placeholder="e.g. Meeting Room A, Floor 3"
            value={lostForm.lostLocation} onChange={e => setLostForm(p => ({ ...p, lostLocation: e.target.value }))} />
          <Textarea label={t("assets.additionalNotes", "Additional Notes")} placeholder={t("assets.describeCircumstances", "Describe the circumstances...")}
            value={lostForm.lostNote} onChange={e => setLostForm(p => ({ ...p, lostNote: e.target.value }))} rows={3} />
          <Box>
            <Text size="sm" fw={500} mb={6}>{t('lostReport.incidentReport')}</Text>
            <FileButton onChange={setLostIncidentFile} accept="application/pdf">
              {(props) => (
                <Button {...props} variant="outline" color="gray" size="xs" leftSection={<IconFile size={14} />} fullWidth>
                  {lostIncidentFile ? lostIncidentFile.name : 'Upload Incident Report (.pdf)'}
                </Button>
              )}
            </FileButton>
            {lostIncidentFile && (
              <Text size="xs" c="dimmed" mt={4}>{(lostIncidentFile.size / 1024).toFixed(0)} KB · will upload on submit</Text>
            )}
          </Box>
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeLost}>{t("common.cancel", "Cancel")}</Button>
            <Button loading={lostSaving} color="grape" leftSection={<IconBan size={15} />} onClick={handleSubmitLost}>
              {t("assets.reportLost","Report Lost")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Mark as Found Modal ───────────────────────────────────────────── */}
      <Modal opened={foundOpened} onClose={closeFound} size={isSmall ? '100%' : 'sm'} fullScreen={isSmall} radius={isMobile ? 0 : 'lg'}
        title={<Group gap={8}><IconCircleCheck size={18} color="#0d9488" /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{t('assets.markAsFound', 'Mark Asset as Found')}</Text></Group>}>
        <Stack gap="sm">
          {foundAsset && (
            <Box p={10} style={{ background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
              <Text size="sm" fw={600}>{foundAsset.name}</Text>
              <Text size="xs" c="dimmed">{foundAsset.assetCode} · {foundAsset.serialNumber}</Text>
            </Box>
          )}
          <Select label={t("assets.assetCondition","Asset Condition")+' *'} value={foundCondition} onChange={v => setFoundCondition(v ?? 'GOOD')}
            data={[
              { value: 'GOOD',    label: '✅ Good — return to Available' },
              { value: 'DAMAGED', label: '⚠️ Damaged — change to Damaged status' },
            ]} />
          <Textarea label={t('lostReport.notes')} placeholder={t("assets.additionalDetailsPlaceholder","Additional details...")}
            value={foundNote} onChange={e => setFoundNote(e.target.value)} rows={2} />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeFound}>{t("common.cancel", "Cancel")}</Button>
            <Button loading={foundSaving} color="teal" leftSection={<IconCircleCheck size={15} />} onClick={handleSubmitFound}>
              {t("assets.confirmFound","Confirm Found")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Report Damaged Modal ──────────────────────────────────────────── */}
      <Modal opened={damagedOpened} onClose={closeDamaged} size={isSmall ? '100%' : 'sm'} fullScreen={isSmall} radius={isMobile ? 0 : 'lg'}
        title={<Group gap={8}><IconAlertTriangle size={18} color="#ef4444" /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{t('lostReport.reportDamaged')}</Text></Group>}>
        <Stack gap="sm">
          {damagedAsset && (
            <Box p={10} style={{ background: '#fff1f2', borderRadius: 8, border: '1px solid #fecdd3' }}>
              <Text size="sm" fw={600}>{damagedAsset.name}</Text>
              <Text size="xs" c="dimmed">{damagedAsset.assetCode} · {damagedAsset.serialNumber}</Text>
            </Box>
          )}
          <SmartDateInput label={t("assets.dateDamaged", "Date Damaged")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={damagedForm.damagedDate}
            onChange={v => setDamagedForm(p => ({ ...p, damagedDate: v }))} clearable />
          <Textarea label={t('lostReport.damageDesc')} placeholder={t("assets.describeDamagePlaceholder","Describe the damage in detail...")}
            value={damagedForm.damagedNote} onChange={e => setDamagedForm(p => ({ ...p, damagedNote: e.target.value }))} rows={3} />
          <Box>
            <Text size="sm" fw={500} mb={6}>{t('lostReport.incidentReport')}</Text>
            <FileButton onChange={setDamagedIncidentFile} accept="application/pdf">
              {(props) => (
                <Button {...props} variant="outline" color="gray" size="xs" leftSection={<IconFile size={14} />} fullWidth>
                  {damagedIncidentFile ? damagedIncidentFile.name : 'Upload Incident Report (.pdf)'}
                </Button>
              )}
            </FileButton>
            {damagedIncidentFile && (
              <Text size="xs" c="dimmed" mt={4}>{(damagedIncidentFile.size / 1024).toFixed(0)} KB · will upload on submit</Text>
            )}
          </Box>
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeDamaged}>{t("common.cancel", "Cancel")}</Button>
            <Button loading={damagedSaving} color="red" leftSection={<IconAlertTriangle size={15} />} onClick={handleSubmitDamaged}>
              {t("assets.reportDamaged","Report Damaged")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Repair Job Modal */}
      <RepairJobModal
        opened={repairModalOpen}
        onClose={() => setRepairModalOpen(false)}
        onSuccess={() => { clearSelection(); loadAssets(page) }}
        repairAssets={repairAssetsList}
        preselectedAssetId={firstSelectedId}
        vendors={repairVendors}
        consumableItems={repairConsumables}
        allAssets={repairAllAssets}
        mode="simple"
      />

      {/* ── Asset Quick View Drawer ── */}
      <AssetQuickView
        asset={quickViewAsset}
        opened={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        onEdit={(a) => openEdit(a)}
        onClone={(a) => openClone(a)}
        onRepair={(a) => openRepairForAsset(a)}
        onSwap={(a) => { setQuickViewOpen(false); openSwapModal(a) }}
        onHistory={(a) => handleOpenHistory(a)}
        onQR={(a) => { setQrAssets([a]); openQR() }}
        onLocation={(a) => handleOpenLocation(a)}
        onLost={(a) => handleOpenLost(a)}
        onFound={(a) => handleOpenFound(a)}
        onInstall={(a) => handleOpenInstall(a)}
        onUninstall={(a) => handleUninstall(a)}
        onDelete={roles.includes('ADMIN') ? (a) => handleHardDelete(a) : undefined}
      />

      {/* ── Swap Asset Modal ── */}
      <SwapAssetModal
        opened={swapModalOpen}
        onClose={() => { setSwapModalOpen(false); setSwapAssetA(null) }}
        onSuccess={() => { clearSelection(); loadAssets(page) }}
        assetA={swapAssetA}
      />

    </Box>
  )
}
